import { $, aviso, tengoRol, esc, fecha, limpiar, noContesto, num, ordenarPor, pedir, pedirTodo, puede, rpc, rpcLee, sb } from './nucleo';
import { ajustable } from './columnas';
import { sugerir, type Sugerencia } from './sugerir';

// =====================================================================
//  INGRESOS POR FOTO  (0076 lectura, 0077 validación: pasos 2 y 3 del plan)
// =====================================================================
//  Una tanda de fotos de remitos (WhatsApp) = un `foto_reporte`. Cada foto se
//  comprime en el navegador, se manda a la edge function `leer_foto`, que la
//  lee dos veces con Haiku (y desempata con Sonnet si no coinciden), controla
//  el total al pie, detecta duplicadas dentro del reporte y matchea cada
//  línea contra `foto_alias` SÓLO por coincidencia exacta. Lo que no matchea
//  queda `a_validar`. La pantalla no decide nada de eso: muestra lo que la
//  función contestó y lo que quedó guardado.
//
//  VALIDAR (paso 3, 0077): lo pendiente se resuelve en la ventana «A validar»
//  (foto a la izquierda, buscador del catálogo a la derecha). Cada acción es
//  una función de la base; la pantalla no decide nada: confirmar guarda el
//  alias y la base resuelve sola las demás líneas con la misma clave. El
//  catálogo es el de INCOMING (hoja «Disponible para repo», por la edge
//  function `sheets_leer`) más los productos creados desde acá; se lee una
//  vez por sesión y se guarda como foto en cada reporte que se abre.
//  Desde D198 (sesión 136) el buscador suma también los productos de
//  Nubilon (`productos`, los activos): si la planilla falla, se sigue
//  pudiendo buscar. La base ya los aceptaba (`app.foto_codigo_existe`).
//  Cada línea llega con un producto SUGERIDO (`sugerir.ts`, D189: modelo,
//  capacidad y color leídos del texto): queda elegido, pero se guarda recién
//  cuando el usuario toca «Confirmar».
//
//  LÍNEA A MANO (0081, D191): lo que la foto no trae o no se pudo leer se
//  agrega con «Agregar línea» en la foto: producto del catálogo y cantidad.
//  Nace validada (`a_mano`), suma en el total de esa foto y no se deshace:
//  si está mal, se ignora.
//
//  CARGA SIN FOTO (0098, D217): lo que no viene en ninguna foto se carga con
//  «Cargar productos sin foto», incluso en un reporte vacío. Va a una tarjeta
//  propia, «Sin foto» (la `foto_imagen` con orden 0, que la base crea con la
//  primera línea): las líneas son como las de «Agregar línea». La ventana
//  queda abierta para cargar uno detrás de otro.
//
//  LA PESTAÑA EN INCOMING (paso 4, 0082, D192): con el reporte completo se
//  muestra lo que va (resumen por producto, de `foto_pestana`) y «Escribir en
//  INCOMING» le pide a `sheets_leer` que agregue una pestaña nueva con el
//  resumen y el detalle por línea. Cada vez es una pestaña nueva. Desde D202
//  (sesión 140, 0088) hay un segundo botón, «con los neteos»: el mismo
//  resumen con los neteos validados aplicados (`foto_pestana_neteada`).
//
//  LA COMPARATIVA CONTRA EL PISTOLEADO (paso 5, 0083, D193): con el reporte
//  completo, «Comparar con el Pistoleado» lee la hoja (`sheets_leer`, acción
//  `pistoleado`), la guarda en el reporte (`foto_guardar_pistoleo`) y la base
//  devuelve una fila por producto: fotos, pistoleadas y la diferencia; arriba
//  lo que no coincide. Al abrir el reporte se muestra la última que se hizo.
//  Desde D196 (sesión 132) el resumen y la comparativa van por categoría; las
//  diferencias, en tres secciones (falta / de más / no coincide), ordenables
//  por columna, y se copian como imagen o texto para WhatsApp. Lo que
//  coincide va en otra tabla. Los anchos se ajustan (`columnas.ts`).
//  Desde D197 (sesión 134, 0085) los NETEOS: lo que se pistoleó de más cubre
//  lo que faltó de otro producto. La base sugiere (mismo modelo y capacidad)
//  y el administrador global valida uno por uno, rechaza o netea a mano; un
//  neteo validado se deshace. Lo neteado entero pasa a «Neteado»; las copias
//  suman los neteos validados en otra sección.
//
//  LA CONFIGURACIÓN (paso 6, 0084, D194): al pie de la pestaña, el modelo de
//  IA, si se guardan las fotos y cuántos días, la planilla y los nombres de
//  sus hojas. La lee y la guarda la base (`foto_leer_config`,
//  `foto_guardar_config`); las edge functions la usan en la próxima lectura.
//
//  LA LIMPIEZA (0091, D208): la primera vez en el día que un administrador
//  abre Fotos, la base dice que toca (`limpieza_toca` en la configuración) y
//  la pantalla le pide a `leer_foto` (acción `limpiar`), en segundo plano, que
//  borre del bucket las fotos vencidas y las sueltas. Se borra sólo la
//  imagen: la foto queda con lo leído y dice «imagen borrada por antigüedad».
//
//  EN PESTAÑAS, COMPACTA (0093, D210, sesión 147): el reporte se trabaja en
//  tres pestañas, en el orden del trabajo: Fotos (cada foto, una fila que se
//  abre al tocarla), Comparativa (la imagen arriba; los neteos, la tabla de
//  diferencias y lo que coincide en plegables) e INCOMING (los dos botones y
//  el resumen plegable). Los neteos validados del mismo par llegan sumados de
//  la base. El reporte se renombra (`foto_renombrar_reporte`) y se borra
//  (`foto_borrar_reporte`, sólo el administrador global) desde arriba.

type Reporte = { id: number; nombre: string; estado: string; creado_en: string; hoja_sheets: string | null };
type Linea = {
  id: number; cantidad_leida: number | null; orden: number; descripcion: string; codigo_proveedor: string | null; cantidad: number | null;
  truncada: boolean; producto_codigo: string | null; estado: string; a_mano: boolean;
};
type Imagen = {
  id: number | null; orden: number; estado: string; total_pie: number | null; total_leido: number | null;
  cuadra: boolean | null; encabezado: string | null; avisos: string[]; costo_usd: number | null;
  modelo_usado: string | null; storage_path: string | null; imagen_borrada_en?: string | null; lineas: Linea[];
  miniatura?: string;   // data URL de la foto recién soltada (sólo en esta sesión)
  leyendo?: boolean; error?: string;
};

let REPORTES: Reporte[] = [];
let REPORTE: Reporte | null = null;
let IMAGENES: Imagen[] = [];
let COLA: File[] = [];
let LEYENDO = false;
let E_REPORTES = '', E_IMAGENES = '';
//  El desplegable trae los últimos TOPE_REPORTES: si llegaron todos, los más
//  viejos no aparecen y se dice (D122).
const TOPE_REPORTES = 100;
//  Si no se pudo pedir el enlace de las imágenes guardadas, se dice eso y no
//  «sin vista previa», que se leería como una foto sin imagen.
let E_MINIATURAS = '';

const BUCKET = 'fotos-ingresos';
//  (0098, D217) La carga sin foto es la `foto_imagen` con orden 0: las fotos
//  de verdad se numeran desde 1.
const esSinFoto = (i: Imagen) => i.orden === 0 && i.id != null;
const hoyIso = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Argentina/Buenos_Aires' });

//  Lo que se ve en lugar de la foto cuando no hay imagen.
const sinImagen = (i: Imagen) => i.imagen_borrada_en ? `imagen borrada por antigüedad (${esc(fecha(i.imagen_borrada_en))})`
  : i.storage_path ? (E_MINIATURAS ? `no se pudo pedir la imagen (${esc(E_MINIATURAS)})` : 'sin vista previa')
  : 'foto no guardada';

// ---------------------------------------------------------------------
//  Cargas
// ---------------------------------------------------------------------
export async function cargarFotos(){
  //  0110 (D234): Fotos tiene permiso propio, que da el admin global.
  if (!puede('foto','usar')) return;
  limpiar('#fotoRepMsg');
  //  Una vez por sesión: volver a la pestaña no pisa lo que se estaba escribiendo.
  if (!CONFIG) void cargarConfig();
  await cargarReportes();
  pintarReportes();
  if (REPORTE){
    await cargarImagenes(REPORTE.id);
    pintarImagenes();
    asegurarCatalogo();
  }
}

async function cargarReportes(){
  const { data, error } = await pedir(sb.from('foto_reporte')
    .select('id,nombre,estado,creado_en,hoja_sheets').order('id', { ascending: false }).limit(TOPE_REPORTES));
  E_REPORTES = error ? error.message : '';
  REPORTES = error ? [] : (data as Reporte[]);
  if (REPORTE && !REPORTES.some(r => r.id === REPORTE.id)) REPORTE = null;
}

async function cargarImagenes(reporteId: number){
  const { data, error } = await pedir(sb.from('foto_imagen')
    .select('id,orden,estado,total_pie,total_leido,cuadra,encabezado,avisos,costo_usd,modelo_usado,storage_path,imagen_borrada_en,foto_linea(id,orden,descripcion,codigo_proveedor,cantidad,cantidad_leida,truncada,producto_codigo,estado,a_mano)')
    .eq('reporte_id', reporteId).order('orden'));
  E_IMAGENES = error ? error.message : '';
  if (error){ IMAGENES = []; return; }
  //  Las miniaturas de la sesión se conservan: una foto recién leída no tiene
  //  por qué volver a bajarse.
  const minis = new Map(IMAGENES.filter(i => i.id && i.miniatura).map(i => [i.id, i.miniatura]));
  //  Las tarjetas con error no están en la base (la lectura no llegó a
  //  guardarse): se conservan, porque borrarlas sería esconder el error.
  const conError = IMAGENES.filter(i => !i.id && i.estado === 'error');
  IMAGENES = (data as any[]).map(f => ({
    id: f.id, orden: f.orden, estado: f.estado, total_pie: f.total_pie, total_leido: f.total_leido,
    cuadra: f.cuadra, encabezado: f.encabezado, avisos: Array.isArray(f.avisos) ? f.avisos : [],
    costo_usd: f.costo_usd, modelo_usado: f.modelo_usado, storage_path: f.storage_path, imagen_borrada_en: f.imagen_borrada_en,
    lineas: ((f.foto_linea ?? []) as Linea[]).slice().sort((a, b) => a.orden - b.orden),
    miniatura: minis.get(f.id),
  }));
  IMAGENES.push(...conError);
  //  Las guardadas en Storage se muestran con un enlace firmado de una hora.
  E_MINIATURAS = '';
  const storage = (sb as any).storage;
  if (!storage) return;
  const sinMini = IMAGENES.filter(i => !i.miniatura && i.storage_path);
  if (!sinMini.length) return;
  const { data: firmadas, error: eFirmar } = await storage.from(BUCKET)
    .createSignedUrls(sinMini.map(i => i.storage_path), 3600);
  if (eFirmar){ E_MINIATURAS = eFirmar.message || 'Storage no contestó'; return; }
  (firmadas ?? []).forEach((u: any, k: number) => { if (u?.signedUrl) sinMini[k].miniatura = u.signedUrl; });
}

// ---------------------------------------------------------------------
//  Reportes
// ---------------------------------------------------------------------
function pintarReportes(){
  const sel = $('#selFotoReporte');
  if (E_REPORTES){
    sel.innerHTML = '<option value="">No se pudieron leer los reportes</option>';
    aviso('#fotoRepMsg', 'bad', 'No se pudo leer la lista de reportes: ' + E_REPORTES);
  } else {
    sel.innerHTML = '<option value="">— Elegí un reporte —</option>' + REPORTES.map(r =>
      `<option value="${r.id}">${esc(r.nombre)} · ${esc(estadoReporte(r.estado))} · ${esc(fecha(r.creado_en))}</option>`).join('');
    if (!REPORTES.length) aviso('#fotoRepMsg', 'info', 'Todavía no hay ningún reporte. Creá uno y arrastrá las fotos.');
    else if (REPORTES.length >= TOPE_REPORTES)
      aviso('#fotoRepMsg', 'warn', `El desplegable muestra los últimos ${TOPE_REPORTES} reportes: los anteriores no aparecen.`);
  }
  sel.value = REPORTE ? String(REPORTE.id) : '';
  $('#cardFotosSubir').classList.toggle('oculto', !REPORTE);
  if (REPORTE) $('#fotoRepTitulo').textContent = REPORTE.nombre;
  //  (D210) Renombrar y borrar, al lado del desplegable. Borrar, sólo el
  //  administrador global (la base lo vuelve a controlar).
  $('#btnFotoRenombrar').classList.toggle('oculto', !REPORTE);
  $('#btnFotoBorrar').classList.toggle('oculto', !REPORTE || !soyAdminGlobal());
  if (!REPORTE) cerrarEdicion();
}

const soyAdminGlobal = () => tengoRol('admin_global');

// ---------------------------------------------------------------------
//  Renombrar y borrar el reporte (0093, D210)
// ---------------------------------------------------------------------
let GUARDANDO_NOMBRE = false, BORRANDO = false;

function cerrarEdicion(){
  $('#fotoRepFila').classList.remove('oculto');
  $('#fotoRepEditar').classList.add('oculto');
  $('#fotoBorrarConf').innerHTML = '';
}

function abrirRenombrar(){
  if (!REPORTE) return;
  cerrarEdicion();
  limpiar('#fotoRepMsg');
  $('#fotoRepFila').classList.add('oculto');
  $('#fotoRepEditar').classList.remove('oculto');
  const inp = $('#inpFotoNombre');
  inp.value = REPORTE.nombre;
  inp.focus();
  inp.select();
}

async function guardarNombre(){
  if (!REPORTE || GUARDANDO_NOMBRE) return;
  const nombre = ($('#inpFotoNombre').value ?? '').trim();
  if (!nombre){ aviso('#fotoRepMsg', 'bad', 'El nombre del reporte no puede quedar vacío.'); return; }
  const rep = REPORTE.id;
  GUARDANDO_NOMBRE = true;
  $('#btnFotoGuardarNombre').disabled = true;
  const { data, error } = await rpc('foto_renombrar_reporte', { p_reporte_id: rep, p_nombre: nombre });
  GUARDANDO_NOMBRE = false;
  $('#btnFotoGuardarNombre').disabled = false;
  if (error){ aviso('#fotoRepMsg', 'bad', 'No se pudo renombrar: ' + error.message); return; }
  cerrarEdicion();
  await cargarReportes();
  if (REPORTE?.id === rep) REPORTE = REPORTES.find(r => r.id === rep) ?? REPORTE;
  pintarReportes();
  pintarPestana();   // la imagen de la comparativa lleva el nombre
  aviso('#fotoRepMsg', 'ok', (data as any)?.mensaje ?? 'Listo.');
}

//  Primer paso: la confirmación, con qué se borra y si ya se escribió en
//  INCOMING (la pestaña queda en la planilla).
function pedirBorrar(){
  if (!REPORTE) return;
  cerrarEdicion();
  limpiar('#fotoRepMsg');
  if (LEYENDO || COLA.length){ aviso('#fotoRepMsg', 'warn', 'Esperá a que terminen de leerse las fotos.'); return; }
  if (ESCRIBIENDO || COMPARANDO || NETEANDO){ aviso('#fotoRepMsg', 'warn', 'Esperá a que termine lo que se está haciendo con el reporte.'); return; }
  const n = IMAGENES.filter(i => i.id && !esSinFoto(i)).length;
  const conSinFoto = IMAGENES.some(esSinFoto);
  //  Lo último que se sabe: si se escribió en esta sesión, lo dice el resumen.
  const hoja = (PESTANA?.reporte === REPORTE.id ? PESTANA.hoja_sheets : null) ?? REPORTE.hoja_sheets;
  $('#fotoBorrarConf').innerHTML = `<div class="foto-conf" role="alertdialog" aria-label="Confirmar el borrado">
    <b>¿Borrar el reporte «${esc(REPORTE.nombre)}»?</b>
    <div class="hint" style="margin:2px 0 0.353rem">Se borran ${n === 0 ? '' : n === 1 ? 'su foto, ' : `sus ${num(n)} fotos, `}${conSinFoto ? 'lo cargado sin foto, ' : ''}lo validado, la comparativa y los neteos. No se puede deshacer.
      ${hoja ? `<b>Ya se escribió en INCOMING</b> (pestaña «${esc(hoja)}»): esa pestaña queda en la planilla.` : ''}</div>
    <div class="fila-acc" style="justify-content:flex-start">
      <button class="b b-peligro" type="button" id="btnFotoBorrarSi">Sí, borrar</button>
      <button class="b b-sec" type="button" id="btnFotoBorrarNo">Cancelar</button>
    </div></div>`;
}

async function borrarReporte(){
  if (!REPORTE || BORRANDO) return;
  const rep = REPORTE.id;
  BORRANDO = true;
  $('#btnFotoBorrarSi').disabled = true;
  const { data, error } = await rpc('foto_borrar_reporte', { p_reporte_id: rep });
  BORRANDO = false;
  if (error){
    const b = document.getElementById('btnFotoBorrarSi') as HTMLButtonElement | null;
    if (b) b.disabled = false;
    aviso('#fotoRepMsg', 'bad', 'No se pudo borrar: ' + error.message);
    return;
  }
  cerrarEdicion();
  if (REPORTE?.id === rep){ REPORTE = null; IMAGENES = []; }
  await cargarReportes();
  pintarReportes();
  aviso('#fotoRepMsg', 'ok', (data as any)?.mensaje ?? 'Reporte borrado.');
}

const estadoReporte = (e: string) =>
  ({ borrador: 'borrador', validando: 'a validar', completo: 'completo' } as Record<string, string>)[e] ?? e;

async function nuevoReporte(){
  const b = $('#btnFotoNuevoReporte');
  b.disabled = true;
  limpiar('#fotoRepMsg');
  //  El nombre («Rep AAAA-MM-DD #n») lo decide la base con el día de la
  //  empresa; la pantalla no escribe la tabla (la puerta es una lista blanca).
  const { data, error } = await rpc('foto_nuevo_reporte', {});
  if (error){
    aviso('#fotoRepMsg', 'bad', (noContesto(error) ? '' : 'No se pudo crear el reporte: ') + error.message);
    if (!noContesto(error)) b.disabled = false;
    return;
  }
  b.disabled = false;
  const d = data as any;
  IMAGENES = [];
  cerrarEdicion();
  nuevaVista();
  await cargarReportes();
  REPORTE = REPORTES.find(r => r.id === Number(d?.id)) ?? null;
  pintarReportes();
  pintarImagenes();
  if (d?.mensaje) aviso('#fotoRepMsg', 'ok', d.mensaje);
}

async function elegirReporte(id: number | null){
  REPORTE = REPORTES.find(r => r.id === id) ?? null;
  IMAGENES = [];
  cerrarEdicion();
  limpiar('#fotoRepMsg');
  nuevaVista();
  pintarReportes();
  if (REPORTE){
    const rep = REPORTE.id;
    await cargarImagenes(rep);
    //  Si mientras tanto se eligió otro, lo de éste ya no va.
    if (REPORTE?.id !== rep) return;
    pintarImagenes();
    asegurarCatalogo();
  }
}

// ---------------------------------------------------------------------
//  Fotos: comprimir, mandar, mostrar
// ---------------------------------------------------------------------
//  A ~1600 px de lado mayor y JPEG 0.82 una foto de WhatsApp queda en
//  100–250 KB: alcanza para leerla y no pesa en Storage (plan, sección 2).
async function comprimir(archivo: File): Promise<{ b64: string; dataUrl: string; bytes: number }>{
  const bitmap = await createImageBitmap(archivo);
  const MAX = 1600;
  const k = Math.min(1, MAX / Math.max(bitmap.width, bitmap.height));
  const c = document.createElement('canvas');
  c.width = Math.round(bitmap.width * k);
  c.height = Math.round(bitmap.height * k);
  c.getContext('2d')!.drawImage(bitmap, 0, 0, c.width, c.height);
  bitmap.close?.();
  const dataUrl = c.toDataURL('image/jpeg', 0.82);
  const b64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  return { b64, dataUrl, bytes: Math.floor(b64.length * 3 / 4) };
}

async function encolar(archivos: FileList | File[]){
  if (!REPORTE) return;
  const nuevos = Array.from(archivos).filter(f => /^image\/(jpeg|png|webp)$/.test(f.type));
  const rechazados = Array.from(archivos).length - nuevos.length;
  if (rechazados) aviso('#fotoColaMsg', 'warn', `${rechazados} archivo(s) no son fotos (JPG, PNG o WebP) y se ignoraron.`);
  COLA.push(...nuevos);
  procesarCola();
}

async function procesarCola(){
  if (LEYENDO || !REPORTE) return;
  LEYENDO = true;
  const reporteId = REPORTE.id;
  try {
    while (COLA.length && REPORTE && REPORTE.id === reporteId){
      const archivo = COLA.shift()!;
      pintarCola();
      let comp: Awaited<ReturnType<typeof comprimir>>;
      try { comp = await comprimir(archivo); }
      catch (e: any){
        IMAGENES.push(tarjetaError(`No se pudo abrir «${archivo.name}»: ${e?.message ?? e}`));
        pintarImagenes();
        continue;
      }
      const provisoria: Imagen = {
        id: null, orden: (IMAGENES.reduce((m, i) => Math.max(m, i.orden), 0) + 1), estado: 'pendiente',
        total_pie: null, total_leido: null, cuadra: null, encabezado: null, avisos: [], costo_usd: null,
        modelo_usado: null, storage_path: null, lineas: [], miniatura: comp.dataUrl, leyendo: true,
      };
      IMAGENES.push(provisoria);
      pintarImagenes();

      const fn = (sb as any).functions;
      let r: any;
      try {
        r = fn ? await fn.invoke('leer_foto', { body: { accion: 'leer', imagen_base64: comp.b64, media_type: 'image/jpeg', reporte_id: reporteId } })
               : { error: { message: 'sin edge functions en este entorno' } };
      } catch (e: any){ r = { error: { message: e?.message ?? String(e) } }; }

      //  `invoke` deja el error del cuerpo (status ≠ 2xx) en `r.error` sin el
      //  mensaje de la función: se lee del `context` si lo hay.
      let detalle = '';
      if (r?.error){
        try { const j = await r.error.context?.json?.(); detalle = j?.error ?? ''; } catch { /* sin cuerpo */ }
      }
      const idx = IMAGENES.indexOf(provisoria);
      if (r?.error || !r?.data?.ok){
        const msg = detalle || r?.data?.error || r?.error?.message || 'la función no contestó';
        IMAGENES[idx] = { ...provisoria, leyendo: false, estado: 'error', error: msg };
      } else {
        const d = r.data;
        IMAGENES[idx] = {
          id: d.guardado?.imagen_id ?? null, orden: d.guardado?.orden ?? provisoria.orden, estado: d.estado,
          total_pie: d.total_pie, total_leido: d.total_leido, cuadra: d.cuadra, encabezado: null,
          avisos: d.avisos ?? [], costo_usd: d.costo_usd, modelo_usado: d.modelo_usado,
          storage_path: d.guardado?.storage_path ?? null, lineas: d.lineas ?? [], miniatura: comp.dataUrl,
        };
      }
      pintarImagenes();
    }
  } finally {
    LEYENDO = false;
    pintarCola();
    //  Al terminar la tanda se relee lo guardado: así lo que se muestra es lo
    //  que quedó en la base (encabezados, órdenes), no lo que la pantalla creyó.
    if (REPORTE && REPORTE.id === reporteId && !COLA.length){
      await cargarImagenes(reporteId);
      pintarImagenes();
      asegurarCatalogo();
    }
  }
}

const tarjetaError = (msg: string): Imagen => ({
  id: null, orden: (IMAGENES.reduce((m, i) => Math.max(m, i.orden), 0) + 1), estado: 'error', total_pie: null,
  total_leido: null, cuadra: null, encabezado: null, avisos: [], costo_usd: null, modelo_usado: null,
  storage_path: null, lineas: [], error: msg,
});

function pintarCola(){
  if (!COLA.length && !LEYENDO){ limpiar('#fotoColaMsg'); return; }
  const n = COLA.length + (LEYENDO ? 1 : 0);
  aviso('#fotoColaMsg', 'info', `Leyendo ${n === 1 ? 'una foto' : n + ' fotos'}… cada una tarda entre 10 y 30 segundos.`);
}

// ---------------------------------------------------------------------
//  Catálogo (INCOMING + productos de Nubilon + creados desde acá)
// ---------------------------------------------------------------------
type Prod = { codigo: string; nombre: string; nro_modelo: string | null; nuevo?: boolean };
let CATALOGO: Prod[] | null = null;      // el de INCOMING, leído en esta sesión
let NUBILON: Prod[] | null = null;       // los productos activos de Nubilon (D198)
let NUBILON_ERR = '';
let CATALOGO_ITEMS: any[] = [];          // tal como vino (para guardarlo en cada reporte)
let CATALOGO_EN = '';
let CATALOGO_ERR = '';
let CATALOGO_LEYENDO = false;
let NUEVOS: Prod[] = [];                 // creados desde Ingresos por foto
let NUEVOS_ERR = '';
const GUARDADO_EN = new Set<number>();   // reportes con la foto del catálogo de esta sesión

const pendientesDe = () => IMAGENES.filter(i => SUMAN(i.estado))
  .flatMap(i => i.lineas.filter(l => l.estado === 'a_validar').map(l => ({ img: i, linea: l })));
const SUMAN = (e: string) => e === 'leida' || e === 'a_validar' || e === 'duplicada_sumar';

//  Se lee una vez por sesión (o con «Releer catálogo») y se guarda en cada
//  reporte que se abre: la base valida los códigos contra esa foto, y al
//  guardarla siembra los «# Modelo» y resuelve lo que ya tiene alias.
async function asegurarCatalogo(forzar = false){
  if (!REPORTE || CATALOGO_LEYENDO) return;
  const rep = REPORTE.id;
  if (!forzar && CATALOGO && GUARDADO_EN.has(rep)) return;
  CATALOGO_LEYENDO = true;
  CATALOGO_ERR = '';
  pintarCatalogo();
  //  Los de Nubilon se leen a la par y no dependen de la planilla.
  const nubilon = forzar || !NUBILON ? cargarNubilon() : null;
  try {
    if (forzar || !CATALOGO){
      const fn = (sb as any).functions;
      let r: any;
      try {
        r = fn ? await fn.invoke('sheets_leer', { body: { accion: 'catalogo' } })
               : { error: { message: 'sin edge functions en este entorno' } };
      } catch (e: any){ r = { error: { message: e?.message ?? String(e) } }; }
      let detalle = '';
      if (r?.error){ try { const j = await r.error.context?.json?.(); detalle = j?.error ?? ''; } catch { /* sin cuerpo */ } }
      if (r?.error || !r?.data?.ok || !Array.isArray(r.data.items)){
        CATALOGO_ERR = 'No se pudo leer el catálogo de INCOMING: ' + (detalle || r?.data?.error || r?.error?.message || 'la función no contestó');
        return;
      }
      CATALOGO_ITEMS = r.data.items;
      CATALOGO = r.data.items.map((x: any) => ({ codigo: String(x.codigo), nombre: String(x.nombre ?? ''), nro_modelo: x.nro_modelo ?? null }));
      CATALOGO_EN = new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' });
      GUARDADO_EN.clear();
    }
    const g = await rpc('foto_guardar_catalogo', { p_reporte_id: rep, p_items: CATALOGO_ITEMS });
    if (g.error){ CATALOGO_ERR = 'Se leyó el catálogo pero no se pudo guardar en el reporte: ' + g.error.message; return; }
    GUARDADO_EN.add(rep);
    const nv = await pedir(sb.from('foto_producto_nuevo').select('codigo,nombre').order('id'));
    //  Si falla, se conservan los que ya se conocían y se dice (no se vacía:
    //  el buscador y los nombres de las líneas dependen de ellos).
    NUEVOS_ERR = nv.error ? 'No se pudieron leer los productos creados desde acá: ' + nv.error.message : '';
    if (!nv.error) NUEVOS = (nv.data as any[]).map(x => ({ codigo: x.codigo, nombre: x.nombre, nro_modelo: null, nuevo: true }));
    if (Number((g.data as any)?.resueltas) > 0 && REPORTE?.id === rep){
      await cargarImagenes(rep);
      pintarImagenes();
    }
  } finally {
    await nubilon;
    CATALOGO_LEYENDO = false;
    pintarCatalogo();
    //  Si mientras tanto se eligió otro reporte, ese también necesita su foto
    //  del catálogo (sin ella la base rechaza los códigos de INCOMING).
    if (REPORTE && REPORTE.id !== rep && !GUARDADO_EN.has(REPORTE.id) && !CATALOGO_ERR) asegurarCatalogo();
  }
}

async function cargarNubilon(){
  let r: { data: any[] | null; error: any };
  try { r = await pedirTodo(() => sb.from('productos').select('id,codigo,descripcion,nro_parte').eq('activo', true),
    { por: 'id', orden: ordenarPor('codigo', 'id') }); }
  catch (e: any){ r = { data: null, error: { message: e?.message ?? String(e) } }; }
  const { data, error } = r;
  NUBILON_ERR = error ? 'No se pudieron leer los productos de Nubilon: ' + error.message : '';
  if (error) return;   // si falla al releer, queda lo que ya se tenía
  NUBILON = (data as any[]).map(p => ({ codigo: p.codigo, nombre: p.descripcion, nro_modelo: p.nro_parte ?? null }));
}

function pintarCatalogo(){
  //  La ventana de agregar puede haberse abierto mientras se leía el catálogo.
  const fa = document.getElementById('faSinCat');
  if (fa) fa.innerHTML = avisoSinCatalogo();
  const caja = $('#fotoCatalogo');
  if (!caja) return;
  if (CATALOGO_LEYENDO){ caja.innerHTML = '<span class="hint">Leyendo el catálogo de INCOMING…</span>'; return; }
  const nub = NUBILON_ERR ? `<div class="aviso a-bad">${esc(NUBILON_ERR)}</div>`
    : NUBILON ? ` · ${num(NUBILON.length)} producto(s) de Nubilon` : '';
  if (CATALOGO_ERR){
    caja.innerHTML = `<div class="aviso a-bad">${esc(CATALOGO_ERR)}${!CATALOGO && NUBILON?.length ? ` El buscador usa sólo los ${num(NUBILON.length)} productos de Nubilon.` : ''}
       <button class="b b-sec" type="button" data-acc="releer">Reintentar</button></div>${NUBILON_ERR ? nub : ''}`;
    return;
  }
  caja.innerHTML = CATALOGO
    ? `<span class="hint">Catálogo de INCOMING: ${num(CATALOGO.length)} productos (leído ${esc(CATALOGO_EN)})${NUBILON_ERR ? '' : nub}${NUEVOS.length ? ` · ${NUEVOS.length} creado(s) desde acá` : ''}.</span>
       <button class="b b-sec" type="button" data-acc="releer">Releer catálogo</button>${NUBILON_ERR ? nub : ''}${
       NUEVOS_ERR ? `<div class="aviso a-bad">${esc(NUEVOS_ERR)}</div>` : ''}`
    : '';
}

//  El aviso de las ventanas «A validar» y «Agregar línea» cuando falta el
//  catálogo de INCOMING: si están los de Nubilon, se busca igual en ellos.
function avisoSinCatalogo(){
  if (CATALOGO) return '';
  const motivo = CATALOGO_LEYENDO ? 'Leyendo el catálogo de INCOMING…' : (CATALOGO_ERR || 'Todavía no se leyó el catálogo.');
  return `<div class="aviso a-warn">${esc(motivo)}${NUBILON?.length ? ` Mientras tanto se busca en los ${num(NUBILON.length)} productos de Nubilon.` : ''}</div>`;
}

//  Busca por código, nombre o # Modelo: todas las palabras tienen que estar.
//  El código exacto va primero. Sólo filtra lo que el usuario escribe (la
//  sugerencia de cada línea es `sugerir`, aparte).
const normB = (t: string) => t.toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Z0-9]+/g, ' ').trim();
function buscar(q: string): Prod[]{
  const todos = todosLosProductos();
  const palabras = normB(q).split(' ').filter(Boolean);
  if (!palabras.length) return [];
  const qn = normB(q);
  return todos
    .filter(p => { const t = normB(`${p.codigo} ${p.nombre} ${p.nro_modelo ?? ''}`); return palabras.every(w => t.includes(w)); })
    .sort((a, b) => (Number(normB(b.codigo) === qn || normB(b.nro_modelo ?? '') === qn) - Number(normB(a.codigo) === qn || normB(a.nro_modelo ?? '') === qn))
                    || a.codigo.localeCompare(b.codigo))
    .slice(0, 12);
}
//  Un código, una vez: manda el de INCOMING (su nombre y su «# Modelo»), después
//  el creado desde acá (lleva la marca) y después el de Nubilon.
const todosLosProductos = () => {
  const vistos = new Set<string>();
  return [...(CATALOGO ?? []), ...NUEVOS, ...(NUBILON ?? [])]
    .filter(p => !vistos.has(p.codigo) && !!vistos.add(p.codigo));
};
const productoDe = (codigo: string) => todosLosProductos().find(p => p.codigo === codigo) ?? null;
const nombreDe = (codigo: string | null) => codigo ? productoDe(codigo)?.nombre ?? '' : '';

// ---------------------------------------------------------------------
//  Pintado
// ---------------------------------------------------------------------
const CHIP: Record<string, [string, string]> = {
  pendiente:       ['c-pen',  'leyendo…'],
  leida:           ['c-ok',   'leída'],
  a_validar:       ['c-warn', 'en duda: revisá las cantidades'],
  duplicada:       ['c-info', 'duplicada (no suma)'],
  duplicada_sumar: ['c-ok',   'duplicada, suma igual'],
  error:           ['c-bad',  'error'],
};
const CHIP_LINEA: Record<string, [string, string]> = {
  auto:           ['c-ok',   'auto'],
  a_validar:      ['c-warn', 'a validar'],
  validada:       ['c-ok',   'validada'],
  ignorada:       ['c-pen',  'ignorada'],
  producto_nuevo: ['c-info', 'producto nuevo'],
};
const chip = (tabla: Record<string, [string, string]>, e: string) => {
  const [c, t] = tabla[e] ?? ['c-pen', e];
  return `<span class="chip ${c}">${esc(t)}</span>`;
};
const usd = (n: number | null | undefined) => n == null ? '' : 'USD ' + Number(n).toFixed(4);

// ---------------------------------------------------------------------
//  Las tres pestañas y los plegables (0093, D210)
// ---------------------------------------------------------------------
type Tab = 'fotos' | 'comparativa' | 'incoming';
let TAB: Tab = 'fotos';
let TAB_DE: number | null = null;          // el reporte para el que ya se eligió la pestaña de entrada
const ABIERTAS = new Set<string>();       // las fotos abiertas (todas cerradas por omisión)
const PLEG_OMISION = { neteos: true, dif: false, bien: false, resumen: false };
let PLEG: Record<string, boolean> = { ...PLEG_OMISION };
const claveTab = (rep: number) => 'nubilon.fotos.pestana.' + rep;

//  Al abrir otro reporte: las fotos cerradas, los plegables como vienen y la
//  pestaña de entrada se vuelve a elegir.
function nuevaVista(){
  //  Lo del reporte anterior no decide nada del nuevo.
  PESTANA = null; E_PESTANA = ''; COMPARA = null; E_COMPARA = '';
  ABIERTAS.clear();
  PLEG = { ...PLEG_OMISION };
  TAB_DE = null;
  TAB = 'fotos';
}

//  Comparativa e INCOMING, con el reporte completo (o para mostrar el error).
const hayPestanas = () => !!E_PESTANA || !!(PESTANA?.completo && PESTANA.reporte === REPORTE?.id);

//  La de entrada, una vez por reporte: con algo pendiente, Fotos; si no, la
//  última que se eligió en este navegador; si no, la Comparativa si ya se
//  comparó. Se recuerda sólo por comodidad.
function elegirTabDeEntrada(){
  if (!REPORTE || TAB_DE === REPORTE.id) return;
  TAB_DE = REPORTE.id;
  const pendiente = pendientesDe().length > 0 || IMAGENES.some(i => i.estado === 'a_validar');
  let t: Tab = 'fotos';
  if (!pendiente && hayPestanas()){
    let guardada: string | null = null;
    try { guardada = localStorage.getItem(claveTab(REPORTE.id)); } catch { /* sin almacenamiento */ }
    const c = COMPARA && COMPARA.reporte === REPORTE.id ? COMPARA : null;
    t = guardada === 'fotos' || guardada === 'comparativa' || guardada === 'incoming' ? guardada
      : c?.tomado_en ? 'comparativa' : 'fotos';
  }
  TAB = t;
}

function elegirTab(t: Tab){
  if (t !== 'fotos' && !hayPestanas()) return;
  TAB = t;
  if (REPORTE){ try { localStorage.setItem(claveTab(REPORTE.id), t); } catch { /* sin almacenamiento */ } }
  pintarTabs();
}

function pintarTabs(){
  if (TAB !== 'fotos' && !hayPestanas()) TAB = 'fotos';
  const c = COMPARA && COMPARA.reporte === REPORTE?.id ? COMPARA : null;
  const nMal = c?.tomado_en ? c.filas.filter(f => f.estado !== 'ok').length : null;
  $('#tabFotoNFotos').textContent = IMAGENES.length ? String(IMAGENES.length) : '';
  $('#tabFotoNDif').textContent = nMal == null ? '' : nMal ? `${nMal} dif.` : '✓';
  //  (D214) La pestaña escrita ya no dice lo de ahora.
  $('#tabFotoNCambio').textContent = cambioHtml() ? '⚠ cambió' : '';
  const sin = hayPestanas() ? '' : 'Con el reporte completo';
  for (const b of Array.from(document.querySelectorAll('#fotoTabs [data-tab]')) as HTMLButtonElement[]){
    const t = b.dataset.tab as Tab;
    const on = t === TAB;
    b.setAttribute('aria-selected', String(on));
    b.tabIndex = on ? 0 : -1;
    b.disabled = t !== 'fotos' && !!sin;
    b.title = t !== 'fotos' ? sin : '';
  }
  $('#fotoPanelFotos').classList.toggle('oculto', TAB !== 'fotos');
  $('#fotoPanelComparativa').classList.toggle('oculto', TAB !== 'comparativa');
  $('#fotoPanelIncoming').classList.toggle('oculto', TAB !== 'incoming');
}

//  Un plegable: abierto o cerrado como lo dejó el usuario (se repinta seguido).
const pleg = (k: string, titulo: string, extra: string, cuerpo: string) =>
  `<details class="pleg" data-pleg="${k}"${PLEG[k] ? ' open' : ''}><summary>${titulo}${extra ? ` <span class="hint">${extra}</span>` : ''}</summary>
     <div class="dentro">${cuerpo}</div></details>`;

const claveFoto = (i: Imagen) => String(i.id ?? 'x' + i.orden);
function abrirFoto(clave: string){
  const tarj = document.querySelector(`.foto-tarj[data-foto="${CSS.escape(clave)}"]`);
  if (!tarj) return;
  const abierta = !ABIERTAS.has(clave);
  if (abierta) ABIERTAS.add(clave); else ABIERTAS.delete(clave);
  tarj.classList.toggle('abierta', abierta);
  tarj.querySelector('.foto-cuerpo')?.classList.toggle('oculto', !abierta);
  tarj.querySelector('.foto-abre')?.setAttribute('aria-expanded', String(abierta));
}

function pintarImagenes(){
  const lista = $('#fotoLista');
  const resumen = $('#fotoResumen');
  pintarCatalogo();
  pintarTabs();
  void refrescarPestana();
  if (E_IMAGENES){
    lista.innerHTML = '';
    resumen.innerHTML = `<div class="aviso a-bad">No se pudieron leer las fotos de este reporte: ${esc(E_IMAGENES)}</div>`;
    return;
  }
  if (!IMAGENES.length){
    lista.innerHTML = '';
    resumen.innerHTML = '<div class="vacio">Este reporte todavía no tiene fotos. Subilas arriba, o cargá los productos con «Cargar productos sin foto».</div>';
    return;
  }
  //  La carga sin foto no es una foto: no entra en la cuenta de fotos.
  const fotos = IMAGENES.filter(i => !esSinFoto(i));
  const cuenta = (e: string) => fotos.filter(i => i.estado === e).length;
  const suman = IMAGENES.filter(i => SUMAN(i.estado));
  const unidades = suman.reduce((s, i) => s + i.lineas.filter(l => l.estado !== 'ignorada').reduce((t, l) => t + (l.cantidad ?? 0), 0), 0);
  const costo = IMAGENES.reduce((s, i) => s + (Number(i.costo_usd) || 0), 0);
  const pend = pendientesDe().length;
  const enDuda = cuenta('a_validar');
  const duplicadas = cuenta('duplicada');
  const truncadas = suman.flatMap(i => i.lineas).filter(l => l.truncada).length;
  const nuevos = suman.flatMap(i => i.lineas).filter(l => l.estado === 'producto_nuevo').map(l => l.producto_codigo);
  //  (D210) El resumen en una línea.
  resumen.innerHTML = `<div class="hint"><b>${fotos.length}</b> foto(s) · ${cuenta('leida')} leídas · ${enDuda} en duda · ${duplicadas + cuenta('duplicada_sumar')} duplicadas${cuenta('error') ? ' · ' + cuenta('error') + ' con error' : ''}${fotos.length < IMAGENES.length ? ' · y lo cargado sin foto' : ''}
    · <b>${num(unidades)}</b> u. <span title="sin duplicadas ni ignoradas">(sin duplicadas ni ignoradas)</span> · lectura <b>${esc(usd(costo))}</b></div>
  ${pend || enDuda ? `<div class="aviso a-warn fila-acc" style="margin-top:0.353rem;justify-content:flex-start;align-items:center">
      <span>${pend ? `<b>${pend}</b> línea(s) a validar.` : ''} ${enDuda ? `<b>${enDuda}</b> foto(s) en duda: revisá sus cantidades y tocá «Confirmar foto».` : ''}</span>
      ${pend ? '<button class="b b-pri" type="button" data-acc="validar">Validar pendientes</button>' : ''}</div>`
    : (REPORTE?.estado === 'completo' || (!pend && !enDuda) ? '<div class="aviso a-ok" style="margin-top:0.353rem">No queda nada pendiente en este reporte.</div>' : '')}
  ${avisosFinales(duplicadas, truncadas, nuevos as string[])}`;

  lista.innerHTML = IMAGENES.map(i => {
    const clave = claveFoto(i);
    const sinFoto = esSinFoto(i);
    const abierta = ABIERTAS.has(clave);
    const mini = sinFoto ? '<div class="mini" title="cargado a mano, sin foto"></div>'
      : i.miniatura
      ? `<img class="mini" src="${esc(i.miniatura)}" alt="Foto ${i.orden}" data-grande="${esc(i.miniatura)}">`
      : `<div class="mini" title="${sinImagen(i)}"></div>`;
    const img = sinFoto ? '<div class="sin-img">cargado a mano, sin foto</div>'
      : i.miniatura
      ? `<img src="${esc(i.miniatura)}" alt="Foto ${i.orden}" data-grande="${esc(i.miniatura)}">`
      : `<div class="sin-img">${sinImagen(i)}</div>`;
    const total = sinFoto ? `${num(i.total_leido ?? 0)} u.` : i.total_pie != null
      ? `total al pie ${num(i.total_pie)} / leído ${num(i.total_leido)} ${i.cuadra ? '✓' : '✗'}`
      : (i.total_leido != null ? `leído ${num(i.total_leido)} (sin total al pie)` : '');
    const filas = i.lineas.map(l => {
      //  La línea a mano no se deshace (no hay nada leído a lo que volver):
      //  se ignora.
      const acc = l.a_mano ? (l.estado === 'ignorada' ? '' : `<button class="b b-sec" type="button" data-acc="ignorar" data-linea="${l.id}">Ignorar</button>`)
        : l.estado === 'a_validar' ? `<button class="b b-pri" type="button" data-acc="resolver" data-linea="${l.id}">Resolver</button>`
        : l.estado === 'auto' ? `<button class="b b-sec" type="button" data-acc="resolver" data-linea="${l.id}">Cambiar</button>`
        : `<button class="b b-sec" type="button" data-acc="reabrir" data-linea="${l.id}">Deshacer</button>`;
      const cant = l.cantidad == null ? '?' : num(l.cantidad);
      const corr = l.cantidad_leida != null ? ` <span class="hint" title="lo que leyó la IA">(leído ${l.cantidad_leida < 0 ? 'nada' : num(l.cantidad_leida)})</span>` : '';
      return `<tr class="${l.estado === 'ignorada' ? 'tachada' : ''}">
        <td>${l.a_mano ? '<span class="chip c-info">agregada a mano</span>' : esc(l.descripcion)}${l.truncada ? ' <span class="chip c-warn">incompleta</span>' : ''}</td>
        <td class="mono">${esc(l.codigo_proveedor ?? '')}</td>
        <td class="num mono">${cant}${corr}</td>
        <td><span class="mono">${esc(l.producto_codigo ?? '—')}</span>${l.producto_codigo ? ` <span class="hint">${esc(nombreDe(l.producto_codigo))}</span>` : ''}</td>
        <td>${chip(CHIP_LINEA, l.estado)}</td>
        <td>${i.estado === 'duplicada' ? '' : acc}</td></tr>`;
    }).join('');
    const aValidar = i.lineas.filter(l => l.estado === 'a_validar').length;
    const cuerpo = i.leyendo ? '<div class="hint">Leyendo la foto…</div>'
      : i.error ? `<div class="aviso a-bad">${esc(i.error)}</div>`
      : `<div class="tw"><table class="t-tarjetas"><thead><tr><th>Leído</th><th>Código</th><th class="num">Cant.</th><th>Producto</th><th>Estado</th><th></th></tr></thead>
         <tbody>${filas || '<tr><td colspan="6" class="vacio">No se leyó ninguna línea</td></tr>'}</tbody></table></div>`;
    const avisos = i.avisos.length ? `<div class="avisos">${i.avisos.map(a => '⚠ ' + esc(a)).join('<br>')}</div>` : '';
    const meta = i.leyendo ? '' : `<div class="meta">${esc(i.modelo_usado ?? '')}${i.costo_usd != null ? ' · ' + esc(usd(i.costo_usd)) : ''}</div>`;
    //  A la vista, lo que pide una acción; «Quitar foto», adentro (para no
    //  tocarlo por error en una lista larga; decisión de Steven, 23/10).
    const botones = (i.leyendo || !i.id) ? '' : `
        ${i.estado === 'a_validar' ? `<button class="b b-pri" type="button" data-acc="confirmar-foto" data-img="${i.id}">Revisé las cantidades: confirmar foto</button>` : ''}
        ${sinFoto ? '<button class="b b-sec" type="button" data-acc="agregar-sin-foto">Agregar línea</button>'
          : SUMAN(i.estado) ? `<button class="b b-sec" type="button" data-acc="agregar" data-img="${i.id}">Agregar línea</button>` : ''}
        ${i.estado === 'duplicada' ? `<button class="b b-sec" type="button" data-acc="sumar" data-img="${i.id}">Sumar igual</button>` : ''}
        ${i.estado === 'duplicada_sumar' ? `<button class="b b-sec" type="button" data-acc="no-sumar" data-img="${i.id}">No sumar</button>` : ''}`;
    const quitar = (i.leyendo || !i.id) ? '' : `<div class="fila-acc" style="justify-content:flex-start;margin-top:0.353rem">
        <button class="b b-peligro" type="button" data-acc="quitar" data-img="${i.id}">${sinFoto ? 'Quitar lo cargado sin foto' : 'Quitar foto'}</button></div>`;
    const resumenFila = i.leyendo ? '' : i.error ? `<span style="color:var(--bad)">${esc(i.error)}</span>`
      : `${i.lineas.length} línea(s)${total ? ' · ' + esc(total) : ''}${aValidar ? ` · <b>${aValidar} a validar</b>` : ''}${i.avisos.length ? ` · <span title="${esc(i.avisos.join(' · '))}">⚠ ${i.avisos.length}</span>` : ''}`;
    return `<div class="foto-tarj${i.leyendo ? ' leyendo' : ''}${abierta ? ' abierta' : ''}" id="foto-${i.id ?? 'x' + i.orden}" data-foto="${esc(clave)}">
      <div class="foto-cab">${mini}<button type="button" class="foto-abre" aria-expanded="${abierta}">${sinFoto ? 'Sin foto' : '#' + i.orden}</button>
        ${sinFoto ? '<span class="chip c-info">a mano</span>' : chip(CHIP, i.estado)} <span class="hint tot">${resumenFila}</span><span class="acc">${botones}</span></div>
      <div class="foto-cuerpo${abierta ? '' : ' oculto'}">${img}<div>${cuerpo}${avisos}${quitar}${meta}</div></div></div>`;
  }).join('');
}

// ---------------------------------------------------------------------
//  La pestaña en INCOMING (paso 4, 0082, D192)
// ---------------------------------------------------------------------
type FilaResumen = { codigo: string; producto: string; cantidad: number };
let PESTANA: { reporte: number; completo: boolean; resumen: FilaResumen[]; total: number; hoja_sheets: string | null;
               sincronizado_en: string | null } | null = null;
let E_PESTANA = '';
let PESTANA_PIDE = 0;
//  (0097, D214) Si lo de hoy ya no es lo que se escribió en INCOMING; lo
//  compara la base (`foto_escrito_cambio`). Nulo: no se sabe.
let ESCRITO: { reporte: number; cambio: boolean | null; con_neteos: boolean | null } | null = null;
let ESCRITO_PIDE = 0;
//  Qué se está escribiendo: '' nada, 'fotos' lo que leyeron las fotos,
//  'neteos' con los neteos validados (D202).
let ESCRIBIENDO: '' | 'fotos' | 'neteos' = '';
type FilaCompara = { codigo: string; producto: string; fotos: number; pistoleado: number; diferencia: number; estado: string;
                     neteado: number; diferencia_final: number };
//  (0085, D197) Un neteo validado y una sugerencia, como los arma la base.
type Neteo = { id: number; sobra_codigo: string; falta_codigo: string; cantidad: number; sobra_producto: string; falta_producto: string;
               sobra_color: string | null; falta_color: string | null; a_revisar: boolean; hecho_por: string | null; hecho_en: string;
               partes?: number };   // (0093, D210) cuántos neteos del mismo par van sumados
type SugNeteo = { sobra_codigo: string; sobra_producto: string; sobra_color: string | null; sobra_precio: number | null;
                  falta_codigo: string; falta_producto: string; falta_color: string | null; falta_precio: number | null;
                  cantidad: number; mismo_precio: boolean };
let COMPARA: { reporte: number; tomado_en: string | null; filas: FilaCompara[]; cuenta: Record<string, number>;
               neteos?: Neteo[]; sugerencias?: SugNeteo[]; puede_netear?: boolean } | null = null;
let E_COMPARA = '';
let COMPARANDO = false;
let NETEANDO = false;

//  Lo pide a la base cada vez que se repinta el reporte: si está completo lo
//  dice ella (y arma el resumen), no la pantalla.
async function refrescarPestana(){
  const rep = REPORTE?.id;
  const yo = ++PESTANA_PIDE;
  if (!rep || !IMAGENES.some(i => i.id) || LEYENDO){
    PESTANA = null; E_PESTANA = ''; COMPARA = null; E_COMPARA = '';
    //  Sin fotos (o leyéndolas), la de entrada es Fotos.
    if (rep && !LEYENDO) elegirTabDeEntrada();
    pintarPestana();
    return;
  }
  const { data, error } = await rpcLee('foto_pestana', { p_reporte_id: rep });
  if (yo !== PESTANA_PIDE || REPORTE?.id !== rep) return;
  E_PESTANA = error ? error.message : '';
  PESTANA = error ? null : { ...(data as any), reporte: rep };
  //  La última comparativa guardada (paso 5), sólo con el reporte completo.
  if (PESTANA?.completo && !COMPARANDO && !NETEANDO){
    const c = await rpcLee('foto_comparativa', { p_reporte_id: rep });
    if (yo !== PESTANA_PIDE || REPORTE?.id !== rep) return;
    E_COMPARA = c.error ? 'No se pudo leer la última comparativa: ' + c.error.message : '';
    COMPARA = c.error ? null : { ...(c.data as any), reporte: rep };
  }
  if (PESTANA?.hoja_sheets) await leerEscrito(rep, false);
  else ESCRITO = null;
  if (yo !== PESTANA_PIDE || REPORTE?.id !== rep) return;
  pintarPestana();
  pedirCategorias();
}

//  (0097, D214) Pregunta si el reporte cambió desde que se escribió. Si no
//  contesta, no se avisa nada (y tampoco se dice que está igual).
async function leerEscrito(rep: number, pintar = true){
  const yo = ++ESCRITO_PIDE;
  const { data, error } = await rpcLee('foto_escrito_cambio', { p_reporte_id: rep });
  if (yo !== ESCRITO_PIDE || REPORTE?.id !== rep) return;
  ESCRITO = error ? null : { reporte: rep, cambio: (data as any)?.cambio ?? null, con_neteos: (data as any)?.con_neteos ?? null };
  //  Sólo el aviso: repintar todo se llevaría el mensaje de lo que se acaba de hacer.
  const caja = document.getElementById('fotoCambioCaja');
  if (pintar && caja){ caja.innerHTML = cambioHtml(); pintarTabs(); }
}

//  (0097, D214) El aviso de que el reporte cambió después de escribirlo.
function cambioHtml(){
  const p = PESTANA;
  if (!p?.hoja_sheets || p.reporte !== REPORTE?.id || ESCRITO?.reporte !== p.reporte || ESCRITO.cambio !== true) return '';
  return `<div class="aviso a-warn" id="fotoCambioEscrito"><b>El reporte cambió después de escribirlo en INCOMING</b>: la pestaña «${esc(p.hoja_sheets)}»${ESCRITO.con_neteos ? ' (con los neteos)' : ''} ya no dice lo de ahora. Si lo volvés a escribir, va a una pestaña nueva.</div>`;
}

// ---------------------------------------------------------------------
//  Las categorías, el orden y las copias (sesión 132, D196)
// ---------------------------------------------------------------------
//  Las dos tablas se cortan por la categoría que el producto tiene en Nubilon
//  (`v_precios`, leída una vez por sesión). Un código que Nubilon no tiene
//  va a «Sin categoría», al final. Si aparece un código que la lectura no
//  traía (un producto creado desde acá), se vuelve a leer una vez.
const SIN_CATEGORIA = 'Sin categoría';
let CATEGORIAS: Map<string, string> | null = null;
let E_CATEGORIAS = '';
const PROBADOS = new Set<string>();
const claveCod = (c: string) => String(c ?? '').trim().toUpperCase();
const categoriaDe = (codigo: string) => CATEGORIAS?.get(claveCod(codigo)) || SIN_CATEGORIA;

let LEYENDO_CATEGORIAS = false;

//  Las tablas se pintan enseguida; si hace falta leer las categorías, se
//  leen aparte (una lectura a la vez) y se repinta al llegar. Un código ya
//  probado no vuelve a pedir la lectura, aunque la anterior haya fallado.
function pedirCategorias(){
  if (LEYENDO_CATEGORIAS || !PESTANA?.completo) return;
  const codigos = [...PESTANA.resumen.map(r => r.codigo), ...(COMPARA?.filas ?? []).map(f => f.codigo)];
  const nuevos = codigos.map(claveCod).filter(k => !PROBADOS.has(k));
  if (!nuevos.length && (CATEGORIAS || E_CATEGORIAS)) return;
  nuevos.forEach(k => PROBADOS.add(k));
  LEYENDO_CATEGORIAS = true;
  void (async () => {
    const { data, error } = await pedirTodo(() => sb.from('v_precios').select('producto_id,codigo,categoria'), { por: 'producto_id' });
    LEYENDO_CATEGORIAS = false;
    if (error) E_CATEGORIAS = error.message;
    else {
      E_CATEGORIAS = '';
      CATEGORIAS = new Map((data as any[]).filter(p => p.categoria).map(p => [claveCod(p.codigo), String(p.categoria)]));
      (data as any[]).forEach(p => PROBADOS.add(claveCod(p.codigo)));
    }
    //  Repintar no se lleva los mensajes que había en pantalla.
    const MSJ = ['fotoPestanaMsg', 'fotoComparaMsg', 'fotoCopiaMsg', 'fotoNeteoMsg'];
    const antes = MSJ.map(id => document.getElementById(id)?.innerHTML ?? '');
    pintarPestana();
    MSJ.forEach((id, k) => { const el = document.getElementById(id); if (el && antes[k]) el.innerHTML = antes[k]; });
  })();
}

//  Por categoría, en orden alfabético y «Sin categoría» al final; adentro, en
//  el orden en que vinieron.
function porCategoria<T extends { codigo: string }>(filas: T[]): { cat: string; filas: T[] }[] {
  const m = new Map<string, T[]>();
  for (const f of filas){
    const c = categoriaDe(f.codigo);
    if (!m.has(c)) m.set(c, []);
    m.get(c)!.push(f);
  }
  return [...m.entries()]
    .sort(([a], [b]) => Number(a === SIN_CATEGORIA) - Number(b === SIN_CATEGORIA) || a.localeCompare(b, 'es'))
    .map(([cat, fs]) => ({ cat, filas: fs }));
}

//  El orden de la comparativa: por omisión, lo que más falta o sobra arriba. Un clic en un título ordena por esa columna; otro
//  clic, al revés. Se ordena adentro de cada sección y categoría.
type ColCompara = 'codigo' | 'producto' | 'fotos' | 'pistoleado' | 'diferencia' | 'diferencia_final';
type Orden = { col: ColCompara; dir: 1 | -1 } | null;
const ORDEN: Record<'dif' | 'bien', Orden> = { dif: null, bien: null };
const comparar = (a: unknown, b: unknown) => typeof a === 'number' && typeof b === 'number'
  ? a - b : String(a ?? '').localeCompare(String(b ?? ''), 'es');
//  Por omisión, lo que más falta o más sobra arriba (después de los neteos).
const ordenadas = (filas: FilaCompara[], o: Orden) => [...filas].sort(o
  ? (a, b) => o.dir * comparar(a[o.col], b[o.col]) || comparar(a.codigo, b.codigo)
  : (a, b) => Math.abs(difFinal(b)) - Math.abs(difFinal(a)) || Math.abs(b.diferencia) - Math.abs(a.diferencia) || comparar(a.codigo, b.codigo));

function ordenarPorColumna(tabla: string, col: string){
  if (tabla !== 'dif' && tabla !== 'bien') return;
  const o = ORDEN[tabla];
  ORDEN[tabla] = { col: col as ColCompara, dir: o?.col === col && o.dir === 1 ? -1 : 1 };
  pintarPestana();
}

//  Las dos secciones de lo que no coincide (pedido de Steven, 19/10, D205):
//  todo lo que falta junto —no se pistoleó o vino menos— y todo lo que sobra
//  junto, según la diferencia que queda después de los neteos validados. Y la
//  de lo que un neteo validado cubre entero (D197), que sólo va en pantalla:
//  las copias la cuentan en «Neteos validados».
const difFinal = (f: FilaCompara) => f.diferencia_final ?? f.diferencia;
const neteadoEntero = (f: FilaCompara) => (f.neteado ?? 0) > 0 && f.diferencia_final === 0;
const SECCIONES = [
  { clase: 'falta', titulo: 'Faltantes', texto: 'el reporte dice más de lo que se pistoleó', es: (f: FilaCompara) => difFinal(f) < 0 },
  { clase: 'sobra', titulo: 'Sobrantes', texto: 'se pistoleó más de lo que dice el reporte', es: (f: FilaCompara) => difFinal(f) > 0 },
];
const NETEADO = { clase: 'neteado', titulo: 'Neteado', texto: 'lo cubre un neteo validado', es: neteadoEntero };
type Seccion = (typeof SECCIONES)[number] & { grupos: { cat: string; filas: FilaCompara[] }[]; productos: number };
//  Lo que ven la tabla y las dos copias, en el mismo orden.
const diferencias = (filas: FilaCompara[], conNeteado = false): Seccion[] => [...SECCIONES, ...(conNeteado ? [NETEADO] : [])]
  .map(s => {
    const fs = filas.filter(f => f.estado !== 'ok' && s.es(f));
    return { ...s, grupos: porCategoria(ordenadas(fs, ORDEN.dif)), productos: fs.length };
  })
  .filter(s => s.grupos.length);
const suma = (fs: FilaCompara[], k: 'fotos' | 'pistoleado' | 'diferencia' | 'diferencia_final') => fs.reduce((t, f) => t + (f[k] ?? 0), 0);
//  Los neteos que cuentan (los validados que no quedaron «a revisar»).
const neteosQueCuentan = (c: NonNullable<typeof COMPARA>) => (c.neteos ?? []).filter(n => !n.a_revisar);
const conColor = (codigo: string, color: string | null) => codigo + (color ? ` (${color})` : '');
//  «2 netean con 15128P (Pink)»: con qué netea cada fila.
const neteoDe = (c: NonNullable<typeof COMPARA>, f: FilaCompara) => neteosQueCuentan(c)
  .flatMap(n => claveCod(n.falta_codigo) === claveCod(f.codigo) ? [`${num(n.cantidad)} netean con ${conColor(n.sobra_codigo, n.sobra_color)}`]
              : claveCod(n.sobra_codigo) === claveCod(f.codigo) ? [`${num(n.cantidad)} netean con ${conColor(n.falta_codigo, n.falta_color)}`] : [])
  .join(' · ');
const signo = (n: number) => (n > 0 ? '+' : '') + num(n);

//  (D202, D209) Por qué todavía no se puede escribir la versión con neteos; vacío
//  si se puede. La base lo vuelve a controlar (`foto_pestana_neteada`).
function sinNeteosPorque(): string {
  const c = COMPARA && COMPARA.reporte === REPORTE?.id ? COMPARA : null;
  if (!c?.tomado_en) return 'Primero comparar con el Pistoleado y validar los neteos.';
  const rev = (c.neteos ?? []).filter(n => n.a_revisar).length;
  if (rev) return `Hay ${rev} neteo(s) «a revisar»: deshacelos o revisalos antes.`;
  //  Sin neteos validados también se puede (D209, 0092): sale el resumen de
  //  siempre, con «(neteado)» en el nombre.
  return '';
}

//  (D210) Pinta las pestañas Comparativa e INCOMING (y la de entrada).
function pintarPestana(){
  const inc = document.getElementById('fotoPanelIncoming');
  const cmp = document.getElementById('fotoPanelComparativa');
  if (!inc || !cmp) return;
  if (E_PESTANA){
    inc.innerHTML = cmp.innerHTML = `<div class="aviso a-bad">No se pudo armar el resumen del reporte: ${esc(E_PESTANA)}</div>`;
    elegirTabDeEntrada();
    pintarTabs();
    return;
  }
  if (!PESTANA || !PESTANA.completo || PESTANA.reporte !== REPORTE?.id){
    inc.innerHTML = cmp.innerHTML = '';
    //  Elige la de entrada sólo con lo de este reporte (no con lo del anterior).
    if (REPORTE && PESTANA?.reporte === REPORTE.id) elegirTabDeEntrada();
    pintarTabs();
    return;
  }
  const p = PESTANA;
  const filas = porCategoria(p.resumen).map(g => `<tr class="cat"><td colspan="3">${esc(g.cat)}</td></tr>`
    + g.filas.map(r => `<tr><td class="mono">${esc(r.codigo)}</td><td>${esc(r.producto)}</td><td class="num mono">${num(r.cantidad)}</td></tr>`).join('')
    + `<tr class="subtot"><td></td><td>Total ${esc(g.cat)}</td><td class="num mono">${num(g.filas.reduce((t, r) => t + r.cantidad, 0))}</td></tr>`).join('');
  const sinNeteos = sinNeteosPorque();
  const ultima = p.hoja_sheets
    ? `<div class="hint" style="margin-top:0.235rem" title="Si lo volvés a escribir, va a otra pestaña nueva (la anterior queda).">Última vez: pestaña <b>«${esc(p.hoja_sheets)}»</b>${p.sincronizado_en ? `, ${esc(fecha(p.sincronizado_en))}` : ''}.</div>`
    : '';
  const avisoCat = E_CATEGORIAS ? `<div class="aviso a-warn">No se pudieron leer las categorías de Nubilon (${esc(E_CATEGORIAS)}): ${CATEGORIAS ? 'las de los productos nuevos pueden figurar' : 'todo figura'} en «${SIN_CATEGORIA}».</div>` : '';
  const at1 = `Al terminar, anota el nombre de la pestaña nueva en AT1 de «${CONFIG?.hoja_catalogo || 'Disponible para repo'}».`;
  inc.innerHTML = `<div id="fotoIncoming">
    ${avisoCat}
    <div class="fila-acc" style="justify-content:flex-start">
      <button class="b b-pri" type="button" data-acc="escribir" ${ESCRIBIENDO ? 'disabled' : ''}
        title="Agrega una pestaña nueva en la planilla con el resumen (lo que leyeron las fotos, sumado) y el detalle por línea. ${esc(at1)}">${ESCRIBIENDO === 'fotos' ? 'Escribiendo en INCOMING…' : 'Escribir en INCOMING'}</button>
      <button class="b b-sec" type="button" data-acc="escribir-neteos" ${ESCRIBIENDO || NETEANDO || COMPARANDO || sinNeteos ? 'disabled' : ''}
        title="El mismo resumen con los neteos validados aplicados y la lista de esos neteos; la pestaña lleva «(neteado)» en el nombre. ${esc(at1)}">${ESCRIBIENDO === 'neteos' ? 'Escribiendo con los neteos…' : 'Escribir en INCOMING con los neteos'}</button>
    </div>
    ${sinNeteos ? `<div class="hint" id="fotoSinNeteos">Con los neteos: ${esc(sinNeteos)}</div>` : ''}
    ${ultima}
    <div id="fotoCambioCaja">${cambioHtml()}</div>
    <div id="fotoPestanaMsg"></div>
    ${pleg('resumen', 'Resumen por producto', `${num(p.resumen.length)} productos · ${num(p.total)} u.`,
      `<div class="tw"><table id="tablaResumen"><thead><tr><th>COD Producto</th><th>Producto</th><th class="num">Cantidad</th></tr></thead>
        <tbody>${filas || '<tr><td colspan="3" class="vacio">Ninguna línea suma</td></tr>'}</tbody>
        <tfoot><tr><td></td><td><b>Total</b></td><td class="num mono"><b>${num(p.total)}</b></td></tr></tfoot></table></div>`)}
  </div>`;
  cmp.innerHTML = avisoCat + comparativaHtml();
  ajustable(document.getElementById('tablaResumen') as HTMLTableElement | null, 'fotos-resumen');
  ajustable(document.getElementById('tablaDif') as HTMLTableElement | null, 'fotos-diferencias');
  ajustable(document.getElementById('tablaBien') as HTMLTableElement | null, 'fotos-coincide');
  elegirTabDeEntrada();
  pintarTabs();
  pintarVistaPrevia();
}

const CHIP_COMPARA: Record<string, [string, (f: FilaCompara) => string]> = {
  faltan:          ['c-bad',  f => `faltan ${num(-f.diferencia)}`],
  sobran:          ['c-warn', f => `sobran ${num(f.diferencia)}`],
  solo_fotos:      ['c-bad',  () => 'sólo en el reporte'],
  solo_pistoleado: ['c-warn', () => 'sólo en el Pistoleado'],
  ok:              ['c-ok',   () => 'coincide'],
};

const COLUMNAS: [ColCompara | '' | 'neteo', string][] = [
  ['', ''], ['codigo', 'COD Producto'], ['producto', 'Producto'],
  ['fotos', 'Reporte'], ['pistoleado', 'Pistoleado'], ['diferencia', 'Diferencia'],
];
//  Con neteos, la tabla de diferencias suma la diferencia «Final» (al lado de
//  la original, para que se vea sin correr la tabla) y con qué se neteó.
const COLUMNAS_NETEO: [ColCompara | '' | 'neteo', string][] = [...COLUMNAS, ['diferencia_final', 'Final'], ['neteo', 'Neteo']];
const cabeza = (t: 'dif' | 'bien', cols = COLUMNAS) => '<tr>' + cols.map(([col, txt]) => {
  if (!col) return '<th></th>';
  if (col === 'neteo') return `<th>${txt}</th>`;
  const o = ORDEN[t];
  const sort = o?.col === col ? ` aria-sort="${o.dir === 1 ? 'ascending' : 'descending'}"` : '';
  const n = col === 'codigo' || col === 'producto' ? '' : ' class="num"';
  return `<th${n}${sort}><button type="button" class="th-ord" data-acc="ordenar" data-tabla="${t}" data-col="${col}">${txt}</button></th>`;
}).join('') + '</tr>';

//  `c` sólo en la tabla de diferencias con neteos: suma las dos columnas.
const filaCompara = (f: FilaCompara, c?: NonNullable<typeof COMPARA>) => {
  const [cls, txt] = neteadoEntero(f) ? ['c-ok', () => 'neteado'] : CHIP_COMPARA[f.estado] ?? ['c-pen', () => f.estado];
  //  (D205) Lo que falta, en rojo claro; lo que sobra, en verde claro.
  const tinte = f.estado === 'ok' ? '' : difFinal(f) < 0 ? ' class="fila-falta"' : difFinal(f) > 0 ? ' class="fila-sobra"' : '';
  return `<tr data-estado="${esc(f.estado)}"${tinte}><td><span class="chip ${cls}">${esc(txt(f))}</span></td><td class="mono">${esc(f.codigo)}</td><td>${esc(f.producto)}</td>
    <td class="num mono">${num(f.fotos)}</td><td class="num mono">${num(f.pistoleado)}</td>
    <td class="num mono">${signo(f.diferencia)}</td>${c ? `<td class="num mono">${signo(f.diferencia_final ?? f.diferencia)}</td><td>${esc(neteoDe(c, f))}</td>` : ''}</tr>`;
};
//  Una categoría: su título, sus filas y, si tiene más de una, su total.
const grupoHtml = (g: { cat: string; filas: FilaCompara[] }, c?: NonNullable<typeof COMPARA>) => `<tr class="cat"><td colspan="${c ? 8 : 6}">${esc(g.cat)}</td></tr>`
  + g.filas.map(f => filaCompara(f, c)).join('')
  + (g.filas.length > 1 ? `<tr class="subtot"><td></td><td></td><td>Total ${esc(g.cat)}</td><td class="num mono">${num(suma(g.filas, 'fotos'))}</td>
      <td class="num mono">${num(suma(g.filas, 'pistoleado'))}</td><td class="num mono">${signo(suma(g.filas, 'diferencia'))}</td>${c
        ? `<td class="num mono">${signo(suma(g.filas, 'diferencia_final'))}</td><td></td>` : ''}</tr>` : '');

//  LOS NETEOS (D197): las sugerencias, los validados y el neteo a mano.
const precio = (n: number | null) => (n == null ? 'sin precio' : '$ ' + num(n));
const quedanParaNetear = (c: NonNullable<typeof COMPARA>) => {
  const rev = new Set((c.neteos ?? []).filter(n => n.a_revisar).flatMap(n => [claveCod(n.sobra_codigo), claveCod(n.falta_codigo)]));
  const libres = c.filas.filter(f => !rev.has(claveCod(f.codigo)));
  return { sobran: libres.filter(f => f.diferencia_final > 0), faltan: libres.filter(f => f.fotos > 0 && f.diferencia_final < 0) };
};
//  (D209) En una línea cada cosa: el código chico y el producto al lado, sin
//  saltos de línea y con la letra más chica (pedido de Steven, 23/10).
const netProd = (cod: string, prod: string) => `<span class="mono">${esc(cod)}</span> ${esc(prod || '')}`;
//  (D210) Lo de adentro del plegable «Neteos»; vacío si no hay nada que
//  mostrar ni que netear.
function neteosHtml(c: NonNullable<typeof COMPARA>){
  const sug = c.sugerencias ?? [], val = c.neteos ?? [], puede = !!c.puede_netear;
  const { sobran, faltan } = quedanParaNetear(c);
  if (!sug.length && !val.length && !(puede && sobran.length && faltan.length)) return '';
  const dis = NETEANDO || COMPARANDO ? 'disabled' : '';
  const sugHtml = sug.length ? `<h4>Sugerencias (${sug.length})</h4><div class="tw"><table class="t-tarjetas" id="tablaSug"><thead><tr><th class="num">Cant.</th><th>Vino de más</th><th>Cubre lo que faltó de</th><th>Precio</th>${puede ? '<th></th>' : ''}</tr></thead><tbody>${
      sug.map((x, k) => `<tr><td class="num mono">${num(x.cantidad)}</td>
        <td class="cel-sobra">${netProd(x.sobra_codigo, x.sobra_producto)}</td>
        <td class="cel-falta">${netProd(x.falta_codigo, x.falta_producto)}</td>
        <td>${x.mismo_precio ? `<span class="chip c-ok" title="mismo precio">mismo · ${esc(precio(x.sobra_precio))}</span>`
              : `<span class="chip c-warn" title="${x.sobra_precio == null || x.falta_precio == null ? 'falta el precio' : 'precio distinto'}">${
                  x.sobra_precio == null || x.falta_precio == null ? 'falta precio' : 'distinto'} · ${esc(precio(x.sobra_precio))} / ${esc(precio(x.falta_precio))}</span>`}</td>
        ${puede ? `<td class="acc">
            <button class="b b-pri" type="button" data-acc="netear-sug" data-k="${k}" ${dis}>Validar</button>
            <button class="b b-sec" type="button" data-acc="rechazar-sug" data-k="${k}" title="Rechazar" aria-label="Rechazar" ${dis}>✕</button></td>` : ''}</tr>`).join('')
    }</tbody></table></div>` : '';
  //  (D210) Los del mismo par llegan sumados de la base: «Deshacer» deshace
  //  el grupo entero.
  const valHtml = val.length ? `<h4>Neteos validados (${val.length})</h4>
    <div class="tw"><table class="t-tarjetas" id="tablaNeteos"><thead><tr><th class="num">Cant.</th><th>Vino de más</th><th>Cubre lo que faltó de</th><th>Validó</th>${puede ? '<th></th>' : ''}</tr></thead><tbody>${
      val.map(n => `<tr data-neteo="${n.id}"><td class="num mono">${num(n.cantidad)}</td>
        <td class="cel-sobra">${netProd(n.sobra_codigo, n.sobra_producto)}</td>
        <td class="cel-falta">${netProd(n.falta_codigo, n.falta_producto)}</td>
        <td>${n.a_revisar ? '<span class="chip c-warn" title="Con el Pistoleado nuevo ya no cierra, así que no se descuenta.">a revisar</span> ' : ''}${
          (n.partes ?? 1) > 1 ? `<span class="chip c-info" title="${num(n.partes ?? 1)} neteos del mismo par, sumados">${num(n.partes ?? 1)} sumados</span> ` : ''}${esc(n.hecho_por ?? '—')} <span class="hint">${esc(fecha(n.hecho_en).split(',')[0])}</span></td>
        ${puede ? `<td class="acc"><button class="b b-sec" type="button" data-acc="deshacer-neteo" data-neteo="${n.id}" ${dis}${
          (n.partes ?? 1) > 1 ? ` title="Deshace los ${num(n.partes ?? 1)} juntos"` : ''}>Deshacer</button></td>` : ''}</tr>`).join('')
    }</tbody></table></div>` : '';
  //  Al repintar (un neteo que la base rechazó) queda lo que ya estaba
  //  elegido: los dos arrancan vacíos (D216) y perderlo obliga a elegir de nuevo.
  const antes = (id: string) => (document.getElementById(id) as HTMLSelectElement | null)?.value ?? '';
  const sobraAntes = antes('netSobra'), faltaAntes = antes('netFalta');
  const opcion = (f: FilaCompara, n: number, elegido = '') => `<option value="${esc(f.codigo)}"${f.codigo === elegido ? ' selected' : ''}>${esc(f.codigo)} · ${esc(f.producto)} (${num(n)})</option>`;
  const manoHtml = puede && sobran.length && faltan.length ? `<h4>Netear a mano</h4>
    <div class="fila-acc" style="justify-content:flex-start;flex-wrap:wrap;gap:0.471rem;align-items:flex-end" title="Entre paréntesis, cuántas faltan o sobran: no se puede netear más que eso.">
      <label>Vino de más <select id="netSobra" data-combo><option value="">— elegí el producto —</option>${sobran.map(f => opcion(f, f.diferencia_final, sobraAntes)).join('')}</select></label>
      <label>cubre lo que faltó de <select id="netFalta" data-combo><option value="">— elegí el producto —</option>${faltan.map(f => opcion(f, -f.diferencia_final, faltaAntes)).join('')}</select></label>
      <label>Cantidad <input id="netCant" type="number" min="1" step="1" value="1" style="width:4.118rem"></label>
      <button class="b b-pri" type="button" data-acc="netear-mano" ${dis}>Netear</button>
    </div>` : '';
  return `<div class="neteos">${sugHtml}${valHtml}${manoHtml}<div id="fotoNeteoMsg"></div></div>`;
}

//  (D210) La pestaña Comparativa: arriba el aviso, «Copiar como imagen» y la
//  imagen; debajo, plegables: los neteos (abierto), la tabla de diferencias
//  y lo que coincide (cerrados); al final, «Volver a comparar».
function comparativaHtml(){
  const rep = REPORTE?.id;
  const c = COMPARA && COMPARA.reporte === rep ? COMPARA : null;
  let cuerpo = '';
  if (E_COMPARA) cuerpo = `<div class="aviso a-bad">${esc(E_COMPARA)}</div>`;
  else if (!c || !c.tomado_en) cuerpo = '<div class="hint">Todavía no se comparó este reporte con el Pistoleado.</div>';
  else {
    const secciones = diferencias(c.filas, true);
    const nMal = c.filas.filter(f => f.estado !== 'ok').length;
    const nNeteado = c.filas.filter(neteadoEntero).length;
    const bien = c.filas.filter(f => f.estado === 'ok');
    const k = c.cuenta ?? {};
    //  Con algún neteo que cuenta, la tabla suma «Neteo» y «Final».
    const cn = neteosQueCuentan(c).length ? c : undefined;
    const difHtml = secciones.map(s => `<tr class="seccion s-${s.clase}"><td colspan="${cn ? 8 : 6}">${esc(s.titulo)} <span class="hint">· ${esc(s.texto)} (${s.productos})</span></td></tr>`
      + s.grupos.map(g => grupoHtml(g, cn)).join('')).join('');
    const bienHtml = porCategoria(ordenadas(bien, ORDEN.bien)).map(g => grupoHtml(g)).join('');
    const net = neteosHtml(c);
    cuerpo = `<div class="${nMal ? 'aviso a-warn' : 'aviso a-ok'}" style="margin:0 0 0.353rem">
        ${nMal ? `<b>${nMal}</b> producto(s) no coinciden${nNeteado ? ` (${nNeteado} neteado(s) entero(s))` : ''}` : 'Todo coincide'} · ${bien.length} bien
        · reporte ${num(k.unidades_fotos ?? 0)} u. · Pistoleado ${num(k.unidades_pistoleado ?? 0)} u.
        <span class="hint">· Pistoleado leído ${esc(fecha(c.tomado_en))}</span></div>
      <div class="fila-acc" style="justify-content:flex-start;margin:0.353rem 0">
        <button class="b b-sec" type="button" data-acc="copiar-imagen" title="Se pega en WhatsApp (Ctrl+V) sin que se desacomode nada">Copiar como imagen</button>
      </div>
      <div id="fotoCopiaMsg"></div>
      <div id="fotoVistaPrevia" class="vista-previa">${VISTA && VISTA.reporte === rep ? vistaImg(VISTA) : ''}</div>
      ${net ? pleg('neteos', 'Neteos', `${(c.sugerencias ?? []).length} sugerencia(s) · ${(c.neteos ?? []).length} validado(s)${
          c.puede_netear ? '' : ' · sólo el administrador global los valida'}`, net) : ''}
      ${nMal ? pleg('dif', `Tabla de diferencias <span class="hint">(${nMal})</span>`, '',
          `${ORDEN.dif ? '<div class="fila-acc" style="justify-content:flex-start;margin:0 0 0.353rem"><button class="b b-sec" type="button" data-acc="orden-original">Orden original</button></div>' : ''}
           <div class="tw"><table class="t-tarjetas" id="tablaDif"><thead>${cabeza('dif', cn ? COLUMNAS_NETEO : COLUMNAS)}</thead><tbody>${difHtml}</tbody></table></div>`) : ''}
      ${pleg('bien', `Lo que coincide <span class="hint">(${bien.length})</span>`, '', bien.length
        ? `${ORDEN.bien ? '<div class="fila-acc" style="justify-content:flex-start;margin:0 0 0.353rem"><button class="b b-sec" type="button" data-acc="orden-original-bien">Orden original</button></div>' : ''}
           <div class="tw"><table class="t-tarjetas" id="tablaBien"><thead>${cabeza('bien')}</thead><tbody>${bienHtml}</tbody></table></div>`
        : `<div class="hint">${c.filas.length ? 'Ningún producto coincide.' : 'Ni las fotos ni el Pistoleado traen productos.'}</div>`)}`;
  }
  return `<div id="fotoCompara">
    ${cuerpo}
    <div class="fila-acc" style="justify-content:flex-start;margin-top:0.471rem">
      <button class="b b-sec" type="button" data-acc="pistoleo" ${COMPARANDO || NETEANDO ? 'disabled' : ''}>${COMPARANDO ? 'Leyendo el Pistoleado…' : (c?.tomado_en ? 'Volver a comparar' : 'Comparar con el Pistoleado')}</button>
    </div>
    <div id="fotoComparaMsg"></div></div>`;
}

//  LA COPIA PARA WHATSAPP (D205): una imagen con sólo las diferencias, en el
//  orden de la pantalla. Primero todo lo que falta (en rojo), después todo lo
//  que sobra (en verde), por categoría; los neteos al fondo.
//  Sin totales que sumen productos distintos (pedido de Steven, 19/10). Arriba
//  va directo el nombre del reporte, sin título ni la hora en que se leyó el
//  Pistoleado (sesión 143).
//  Desde la sesión 146 (D209): si no queda nada que falte ni sobre, una banda
//  verde dice «TODO COINCIDE»; los neteos van en una tabla con lo que vino de
//  más en verde claro y lo que faltó del reporte en rojo claro; y la imagen se
//  ve en la pantalla antes de copiarla.
const encabezadoCopia = (c: NonNullable<typeof COMPARA>) => ({
  reporte: REPORTE?.nombre ?? '',
  totales: `Reporte ${num(c.cuenta?.unidades_fotos ?? 0)} u. · Pistoleado ${num(c.cuenta?.unidades_pistoleado ?? 0)} u.`
    + (neteosQueCuentan(c).length ? ` · ${neteosQueCuentan(c).length} neteo(s) validado(s)` : ''),
});
const corto = (t: string) => (t.length > 60 ? t.slice(0, 59) + '…' : t);

//  Colores fijos (no los del tema): la imagen se ve igual en cualquier teléfono.
const TINTA = {
  texto: '#111827', gris: '#6b7280', cat: '#f3f4f6', neteo: '#374151',
  falta: { fuerte: '#b91c1c', claro: '#fee2e2', borde: '#fecaca' },
  sobra: { fuerte: '#15803d', claro: '#dcfce7', borde: '#bbf7d0' },
};
//  La imagen: la tabla dibujada en un lienzo blanco, del ancho justo. Devuelve
//  el lienzo y su ancho en puntos de pantalla (el lienzo va al doble).
function dibujarDiferencias(c: NonNullable<typeof COMPARA>): { cv: HTMLCanvasElement; ancho: number } {
  const e = encabezadoCopia(c);
  const secciones = diferencias(c.filas);
  const LETRA = '-apple-system, "Segoe UI", Roboto, Arial, sans-serif';
  const f = (peso: number, tam: number) => `${peso} ${tam}px ${LETRA}`;
  const cv = document.createElement('canvas');
  const g = cv.getContext('2d');
  if (!g) throw new Error('el navegador no puede dibujar la imagen');
  //  La última columna dice «Faltan» o «Sobran» según la sección, y lleva lo
  //  que queda después de los neteos.
  const cols = [
    { t: 'COD Producto', v: (x: FilaCompara) => x.codigo, der: false },
    { t: 'Producto', v: (x: FilaCompara) => corto(x.producto) + ((x.neteado ?? 0) > 0 ? `  (neteado ${num(x.neteado)})` : ''), der: false },
    { t: 'Reporte', v: (x: FilaCompara) => num(x.fotos), der: true },
    { t: 'Pistoleado', v: (x: FilaCompara) => num(x.pistoleado), der: true },
    { t: 'Faltan', v: (x: FilaCompara) => num(Math.abs(difFinal(x))), der: true },
  ];
  const neteos = neteosQueCuentan(c);
  //  El código va más chico (al jefe casi no le importa) y lo demás, más
  //  grande, para leerlo en el teléfono (pedido de Steven, 19/10).
  const PAD = 14, M = 20, ALTO = 34, BANDA = 36, CAT = 28, SEP = 18;
  const FUENTE = { cod: f(400, 13), fila: f(400, 17), cant: f(800, 19), cabeza: f(700, 13), banda: f(800, 17) };
  const fuenteFila = (i: number) => (i === 0 ? FUENTE.cod : i === 4 ? FUENTE.cant : FUENTE.fila);
  const ancho = (fuente: string, t: string) => (g.font = fuente, g.measureText(t).width);
  const todas = secciones.flatMap(s => s.grupos.flatMap(gr => gr.filas));
  const anchos = cols.map((col, i) => Math.ceil(Math.max(
    ancho(FUENTE.cabeza, col.t.toUpperCase()), i === 4 ? ancho(FUENTE.cabeza, 'SOBRAN') : 0,
    ...todas.map(x => ancho(fuenteFila(i), col.v(x))))) + PAD * 2);
  const banda = (s: Seccion) => s.titulo.toUpperCase();
  //  Sin nada que falte ni sobre, la banda verde de «todo coincide».
  const coincide = neteos.length ? 'TODO COINCIDE CON LOS NETEOS' : 'TODO COINCIDE CON EL PISTOLEADO';
  //  Los neteos: la cantidad, lo que vino de más (verde) y lo que faltó del
  //  reporte y cubre (rojo); en cada uno, el código chico y el producto.
  const NET_T = ['CANT.', 'VINO DE MÁS', 'CUBRE LO QUE FALTÓ DE'];
  const netCelda = (cod: string, prod: string) => ({ cod, prod: corto(prod || '') });
  const netFilas = neteos.map(n => [num(n.cantidad), netCelda(n.sobra_codigo, n.sobra_producto), netCelda(n.falta_codigo, n.falta_producto)] as const);
  const anchoDos = (x: { cod: string; prod: string }) => ancho(FUENTE.cod, x.cod) + (x.prod ? 10 + ancho(FUENTE.fila, x.prod) : 0);
  const netAnchos = [0, 1, 2].map(i => Math.ceil(Math.max(ancho(FUENTE.cabeza, NET_T[i]),
    ...netFilas.map(r => (i === 0 ? ancho(FUENTE.cant, r[0]) : anchoDos(r[i] as { cod: string; prod: string }))))) + PAD * 2);
  const anchoTabla = Math.ceil(Math.max(secciones.length ? anchos.reduce((t, w) => t + w, 0) : 0,
    ...secciones.map(s => ancho(FUENTE.banda, banda(s)) + PAD * 2),
    secciones.length ? 0 : ancho(FUENTE.banda, coincide) + PAD * 2,
    neteos.length ? netAnchos.reduce((t, w) => t + w, 0) : 0));
  //  Si algo es más ancho que la tabla, el sobrante va al producto.
  anchos[1] += anchoTabla - anchos.reduce((t, w) => t + w, 0);
  if (neteos.length) netAnchos[2] += anchoTabla - netAnchos.reduce((t, w) => t + w, 0);
  const anchoTit = Math.max(ancho(f(800, 22), e.reporte), ancho(f(400, 14), e.totales));
  const W = Math.ceil(Math.max(anchoTabla, anchoTit)) + M * 2;
  const renglones = secciones.reduce((t, s) => t + 2 + s.grupos.reduce((u, gr) => u + 1 + gr.filas.length, 0), 0) + neteos.length;
  const ARRIBA = 62;
  const H = M + ARRIBA
    + (secciones.length ? secciones.reduce((t, s) => t + BANDA + ALTO + s.grupos.reduce((u, gr) => u + CAT + gr.filas.length * ALTO, 0) + SEP, 0) : BANDA + SEP)
    + (neteos.length ? BANDA + ALTO * (1 + neteos.length) : 0) + M;
  //  El doble de puntos, para que se lea bien en el teléfono; con muchas filas
  //  menos, para no pasar el tope del navegador (Chrome: 32.767 px de lado;
  //  Safari: 16,7 millones de puntos). Si ni así se lee, se avisa.
  const X = Math.min(2, 32000 / H, Math.sqrt(16_000_000 / (W * H)));
  if (X < 1) throw new Error(`son demasiadas filas para una imagen (${renglones})`);
  cv.width = W * X; cv.height = H * X;
  g.scale(X, X);
  g.fillStyle = '#ffffff'; g.fillRect(0, 0, W, H);
  g.textBaseline = 'middle';
  g.fillStyle = TINTA.texto; g.font = f(800, 22); g.fillText(e.reporte, M, M + 12);
  g.fillStyle = TINTA.gris; g.font = f(400, 14); g.fillText(e.totales, M, M + 40);
  let y = M + ARRIBA;
  const celdas = (vals: string[], color: (i: number) => string, fuente: (i: number) => string) => {
    let x = M;
    vals.forEach((v, i) => {
      g.fillStyle = color(i); g.font = fuente(i);
      if (cols[i].der){ g.textAlign = 'right'; g.fillText(v, x + anchos[i] - PAD, y + ALTO / 2); }
      else { g.textAlign = 'left'; g.fillText(v, x + PAD, y + ALTO / 2); }
      x += anchos[i];
    });
    g.textAlign = 'left';
  };
  const bandaDe = (color: string, texto: string) => {
    g.fillStyle = color; g.fillRect(M, y, anchoTabla, BANDA);
    g.fillStyle = '#ffffff'; g.font = FUENTE.banda; g.fillText(texto, M + PAD, y + BANDA / 2);
    y += BANDA;
  };
  for (const s of secciones){
    const tinta = s.clase === 'falta' ? TINTA.falta : TINTA.sobra;
    //  La banda de la sección, en el color fuerte, y los títulos de columna.
    bandaDe(tinta.fuerte, banda(s));
    celdas(cols.map((col, i) => (i === 4 ? (s.clase === 'falta' ? 'Faltan' : 'Sobran') : col.t).toUpperCase()), () => TINTA.gris, () => FUENTE.cabeza);
    y += ALTO;
    for (const gr of s.grupos){
      g.fillStyle = TINTA.cat; g.fillRect(M, y, anchoTabla, CAT);
      g.fillStyle = TINTA.gris; g.font = FUENTE.cabeza; g.fillText(gr.cat.toUpperCase(), M + PAD, y + CAT / 2);
      y += CAT;
      for (const x of gr.filas){
        g.fillStyle = tinta.claro; g.fillRect(M, y, anchoTabla, ALTO);
        g.strokeStyle = tinta.borde; g.beginPath(); g.moveTo(M, y + ALTO - 0.5); g.lineTo(M + anchoTabla, y + ALTO - 0.5); g.stroke();
        celdas(cols.map(col => col.v(x)), i => (i === 4 ? tinta.fuerte : TINTA.texto), fuenteFila);
        y += ALTO;
      }
    }
    y += SEP;
  }
  if (!secciones.length){ bandaDe(TINTA.sobra.fuerte, coincide); y += SEP; }
  //  Los neteos validados (D197, D209), al fondo: lo que vino de más en verde
  //  claro y lo que faltó del reporte, en rojo claro.
  if (neteos.length){
    bandaDe(TINTA.neteo, 'NETEOS VALIDADOS');
    let x = M;
    NET_T.forEach((t, i) => {
      g.fillStyle = TINTA.gris; g.font = FUENTE.cabeza;
      g.textAlign = i === 0 ? 'right' : 'left';
      g.fillText(t, i === 0 ? x + netAnchos[0] - PAD : x + PAD, y + ALTO / 2);
      x += netAnchos[i];
    });
    g.textAlign = 'left';
    y += ALTO;
    for (const r of netFilas){
      x = M;
      g.fillStyle = TINTA.texto; g.font = FUENTE.cant; g.textAlign = 'right';
      g.fillText(r[0], x + netAnchos[0] - PAD, y + ALTO / 2);
      g.textAlign = 'left';
      x += netAnchos[0];
      ([[r[1], TINTA.sobra], [r[2], TINTA.falta]] as const).forEach(([celda, tinta], k) => {
        const w = netAnchos[k + 1];
        g.fillStyle = tinta.claro; g.fillRect(x, y, w, ALTO);
        g.strokeStyle = tinta.borde; g.beginPath(); g.moveTo(x, y + ALTO - 0.5); g.lineTo(x + w, y + ALTO - 0.5); g.stroke();
        const dx = ancho(FUENTE.cod, celda.cod) + 10;   // antes de elegir la letra del producto: `ancho` la cambia
        g.fillStyle = tinta.fuerte; g.font = FUENTE.cod; g.fillText(celda.cod, x + PAD, y + ALTO / 2);
        g.fillStyle = TINTA.texto; g.font = FUENTE.fila; g.fillText(celda.prod, x + PAD + dx, y + ALTO / 2);
        x += w;
      });
      y += ALTO;
    }
  }
  return { cv, ancho: W };
}
const aBlob = (cv: HTMLCanvasElement) => new Promise<Blob>((ok, mal) => cv.toBlob(b => (b ? ok(b) : mal(new Error('no se pudo armar la imagen'))), 'image/png'));
function imagenDiferencias(c: NonNullable<typeof COMPARA>): Promise<Blob> {
  try { return aBlob(dibujarDiferencias(c).cv); } catch (e){ return Promise.reject(e); }
}

//  (D209) La vista previa: la misma imagen, en la pantalla, debajo del botón.
//  Se rearma cada vez que se repinta la comparativa; mientras llega la nueva
//  queda la anterior, para que no parpadee.
let VISTA: { reporte: number; url: string; ancho: number } | null = null;
let VISTA_PIDE = 0;
const vistaImg = (v: NonNullable<typeof VISTA>) =>
  `<img src="${v.url}" alt="La imagen que se copia para WhatsApp" style="width:${v.ancho}px">`;
function pintarVistaPrevia(){
  const c = COMPARA && COMPARA.reporte === REPORTE?.id ? COMPARA : null;
  if (!c?.tomado_en || !document.getElementById('fotoVistaPrevia')) return;
  const yo = ++VISTA_PIDE, rep = c.reporte;
  let d: { cv: HTMLCanvasElement; ancho: number };
  try { d = dibujarDiferencias(c); } catch (e: any){
    const caja = document.getElementById('fotoVistaPrevia');
    if (caja) caja.innerHTML = `<div class="aviso a-bad">No se pudo armar la imagen: ${esc(e?.message ?? String(e))}</div>`;
    return;
  }
  aBlob(d.cv).then(b => {
    const caja = document.getElementById('fotoVistaPrevia');
    if (yo !== VISTA_PIDE || !caja || REPORTE?.id !== rep) return;
    if (VISTA) URL.revokeObjectURL(VISTA.url);
    VISTA = { reporte: rep, url: URL.createObjectURL(b), ancho: d.ancho };
    caja.innerHTML = vistaImg(VISTA);
  }, (e: any) => {
    const caja = document.getElementById('fotoVistaPrevia');
    if (yo === VISTA_PIDE && caja) caja.innerHTML = `<div class="aviso a-bad">No se pudo armar la imagen: ${esc(e?.message ?? String(e))}</div>`;
  });
}

//  (D205) Sólo como imagen: la copia como texto se sacó (pedido de Steven, 19/10).
async function copiarDiferencias(){
  const c = COMPARA && COMPARA.reporte === REPORTE?.id ? COMPARA : null;
  if (!c?.tomado_en) return;
  const imagen = imagenDiferencias(c);
  try {
    if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') throw new Error('este navegador no copia imágenes');
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': imagen })]);
    aviso('#fotoCopiaMsg', 'ok', 'Imagen copiada: pegala en WhatsApp (Ctrl+V).');
  } catch (e: any){
    //  Si no se puede copiar, se descarga: queda para adjuntarla.
    try {
      const url = URL.createObjectURL(await imagen);
      const a = document.createElement('a');
      a.href = url; a.download = `comparativa ${REPORTE?.nombre ?? ''}.png`.trim();
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      aviso('#fotoCopiaMsg', 'warn', `No se pudo copiar la imagen (${e?.message ?? e}): se descargó como archivo, para adjuntarla en WhatsApp.`);
    } catch (e2: any){
      aviso('#fotoCopiaMsg', 'bad', 'No se pudo armar la imagen: ' + (e2?.message ?? String(e2)));
    }
  }
}

//  Validar una sugerencia, rechazarla, deshacer un neteo o netear a mano
//  (D197). La base contesta la comparativa entera, ya con el cambio.
async function accionNeteo(acc: string, b: HTMLElement){
  const c = COMPARA && COMPARA.reporte === REPORTE?.id ? COMPARA : null;
  if (!c || NETEANDO || COMPARANDO) return;
  const rep = c.reporte;
  let r: { data: any; error: any };
  if (acc === 'deshacer-neteo'){
    NETEANDO = true; pintarPestana();
    r = await rpc('foto_deshacer_neteo', { p_neteo_id: Number(b.dataset.neteo) });
  } else if (acc === 'netear-mano'){
    const cant = Number((document.getElementById('netCant') as HTMLInputElement | null)?.value);
    const sobra = (document.getElementById('netSobra') as HTMLSelectElement | null)?.value ?? '';
    const falta = (document.getElementById('netFalta') as HTMLSelectElement | null)?.value ?? '';
    //  Los dos arrancan vacíos (pedido de Steven, 1/11, D216).
    if (!sobra || !falta){ aviso('#fotoNeteoMsg', 'bad', 'Elegí qué vino de más y qué faltó.'); return; }
    if (!Number.isInteger(cant) || cant < 1){ aviso('#fotoNeteoMsg', 'bad', 'La cantidad a netear tiene que ser un número entero, 1 o más.'); return; }
    NETEANDO = true; pintarPestana();
    r = await rpc('foto_netear', { p_reporte_id: rep, p_sobra: sobra, p_falta: falta, p_cantidad: cant });
  } else {
    const x = (c.sugerencias ?? [])[Number(b.dataset.k)];
    if (!x) return;
    NETEANDO = true; pintarPestana();
    r = acc === 'netear-sug'
      ? await rpc('foto_netear', { p_reporte_id: rep, p_sobra: x.sobra_codigo, p_falta: x.falta_codigo, p_cantidad: x.cantidad })
      : await rpc('foto_rechazar_neteo', { p_reporte_id: rep, p_sobra: x.sobra_codigo, p_falta: x.falta_codigo });
  }
  NETEANDO = false;
  if (REPORTE?.id !== rep) return;
  if (!r.error){ COMPARA = { ...(r.data as any), reporte: rep }; PESTANA_PIDE++; }
  pintarPestana();
  //  (D214) Un neteo cambia la versión con neteos: se vuelve a preguntar.
  if (!r.error && PESTANA?.hoja_sheets) void leerEscrito(rep);
  //  Si ya no queda nada de neteos que mostrar, el aviso va debajo de la comparativa.
  aviso(document.getElementById('fotoNeteoMsg') ? '#fotoNeteoMsg' : '#fotoComparaMsg', r.error ? 'bad' : 'ok', r.error ? 'No se pudo: ' + r.error.message : (r.data as any)?.mensaje ?? 'Listo.');
}

async function compararPistoleado(){
  if (!REPORTE || COMPARANDO || NETEANDO) return;
  const rep = REPORTE.id;
  COMPARANDO = true;
  pintarPestana();
  const fn = (sb as any).functions;
  let r: any;
  try {
    r = fn ? await fn.invoke('sheets_leer', { body: { accion: 'pistoleado' } })
           : { error: { message: 'sin edge functions en este entorno' } };
  } catch (e: any){ r = { error: { message: e?.message ?? String(e) } }; }
  let detalle = '';
  if (r?.error){ try { const j = await r.error.context?.json?.(); detalle = j?.error ?? ''; } catch { /* sin cuerpo */ } }
  let msg = '', bad = false;
  if (r?.error || !r?.data?.ok || !Array.isArray(r.data.items)){
    bad = true;
    msg = 'No se pudo leer el Pistoleado de INCOMING: ' + (detalle || r?.data?.error || r?.error?.message || 'la función no contestó');
  } else {
    const g = await rpc('foto_guardar_pistoleo', { p_reporte_id: rep, p_items: r.data.items });
    if (g.error){ bad = true; msg = 'Se leyó el Pistoleado pero no se pudo guardar en el reporte: ' + g.error.message; }
    else {
      COMPARA = { ...(g.data as any), reporte: rep };
      E_COMPARA = '';
      msg = (g.data as any)?.mensaje ?? 'Listo.';
    }
  }
  COMPARANDO = false;
  //  Una lectura de la comparativa que salió antes de ésta ya no vale.
  PESTANA_PIDE++;
  if (REPORTE?.id !== rep) return;
  pintarPestana();
  aviso('#fotoComparaMsg', bad ? 'bad' : 'ok', msg);
  pedirCategorias();
  //  (D214) Otro Pistoleado cambia la versión con neteos: se vuelve a preguntar.
  if (!bad && PESTANA?.hoja_sheets) void leerEscrito(rep);
}

async function escribirEnIncoming(conNeteos = false){
  if (!REPORTE || ESCRIBIENDO) return;
  const rep = REPORTE.id;
  ESCRIBIENDO = conNeteos ? 'neteos' : 'fotos';
  pintarPestana();
  const fn = (sb as any).functions;
  let r: any;
  try {
    r = fn ? await fn.invoke('sheets_leer', { body: { accion: 'escribir_reporte', reporte_id: rep, ...(conNeteos ? { con_neteos: true } : {}) } })
           : { error: { message: 'sin edge functions en este entorno' } };
  } catch (e: any){ r = { error: { message: e?.message ?? String(e) } }; }
  let detalle = '';
  if (r?.error){ try { const j = await r.error.context?.json?.(); detalle = j?.error ?? ''; } catch { /* sin cuerpo */ } }
  ESCRIBIENDO = '';
  if (REPORTE?.id !== rep) return;
  if (r?.error || !r?.data?.ok){
    pintarPestana();
    aviso('#fotoPestanaMsg', 'bad', 'No se pudo escribir en INCOMING: ' + (detalle || r?.data?.error || r?.error?.message || 'la función no contestó'));
    return;
  }
  const d = r.data;
  await refrescarPestana();
  const link = d.url ? ` <a href="${esc(d.url)}" target="_blank" rel="noopener">Abrir la pestaña</a>` : '';
  const caja = document.getElementById('fotoPestanaMsg');
  if (caja) caja.innerHTML = `<div class="aviso a-ok">${esc(d.mensaje ?? 'Listo.')}${link}</div>`
    //  (D203) La pestaña quedó, pero no se pudo anotar su nombre en AT1.
    + (d.aviso ? `<div class="aviso a-warn">${esc(d.aviso)}</div>` : '');
}

// ---------------------------------------------------------------------
//  La configuración (paso 6, 0084, D194)
// ---------------------------------------------------------------------
type ConfigFotos = { modelo_lectura: string; guardar_fotos: boolean; retencion_dias: number; spreadsheet_id: string;
                     hoja_catalogo: string; hoja_pistoleo: string; actualizado_en: string | null;
                     limpieza_en: string | null; limpieza_borradas: number | null; limpieza_toca: boolean };
let CONFIG: ConfigFotos | null = null;
let E_CONFIG = '';
let LIMPIANDO = false, E_LIMPIEZA = '';

async function cargarConfig(){
  const { data, error } = await rpcLee('foto_leer_config');
  E_CONFIG = error ? error.message : '';
  CONFIG = error ? null : (data as ConfigFotos);
  pintarConfig();
  if (CONFIG?.limpieza_toca) void limpiarVencidas();
  void cargarGrupos();
}

//  (D208) En segundo plano: no frena la pestaña. Si borró alguna foto del
//  reporte abierto, se vuelve a leer para que diga «imagen borrada».
async function limpiarVencidas(){
  if (LIMPIANDO) return;
  const fn = (sb as any).functions;
  if (!fn){ E_LIMPIEZA = 'sin edge functions en este entorno'; pintarLimpieza(); return; }
  LIMPIANDO = true;
  pintarConfig();
  let r: any;
  try { r = await fn.invoke('leer_foto', { body: { accion: 'limpiar' } }); }
  catch (e: any){ r = { error: { message: e?.message ?? String(e) } }; }
  let detalle = '';
  if (r?.error){ try { const j = await r.error.context?.json?.(); detalle = j?.error ?? ''; } catch { /* sin cuerpo */ } }
  E_LIMPIEZA = r?.error || !r?.data?.ok ? (detalle || r?.data?.error || r?.error?.message || 'la función no contestó') : '';
  LIMPIANDO = false;
  //  La configuración trae cuándo se limpió y cuántas borró; se relee sin pisar
  //  lo que se esté escribiendo en el formulario.
  const { data, error } = await rpcLee('foto_leer_config');
  if (error) E_LIMPIEZA = (E_LIMPIEZA ? E_LIMPIEZA + ' · ' : '') + 'no se pudo releer la configuración: ' + error.message;
  else if (data && CONFIG){
    const c = data as ConfigFotos;
    CONFIG = { ...CONFIG, limpieza_en: c.limpieza_en, limpieza_borradas: c.limpieza_borradas, limpieza_toca: c.limpieza_toca };
  }
  pintarLimpieza();
  //  Con un error a mitad de camino, lo que sí se borró ya quedó anotado.
  if ((E_LIMPIEZA || Number(r?.data?.vencidas) > 0) && REPORTE){
    await cargarImagenes(REPORTE.id);
    pintarImagenes();
  }
}

function pintarLimpieza(){
  const caja = document.getElementById('fotoLimpieza');
  if (!caja || !CONFIG) return;
  const c = CONFIG;
  const ultima = LIMPIANDO ? 'Borrando las fotos vencidas…'
    : c.limpieza_en ? `Última limpieza: ${esc(fecha(c.limpieza_en))}`
      + (c.limpieza_borradas == null ? ' (no terminó)' : ` · ${num(c.limpieza_borradas)} borrada(s)`)
    : 'Todavía no se limpió nunca.';
  caja.innerHTML = `<span class="hint">Se borran solas una vez por día, al abrir Fotos: la imagen de las fotos de más de
      ${num(c.retencion_dias)} día(s) (lo leído y validado queda) y las que ya no son de ningún reporte. ${ultima}</span>`
    + (E_LIMPIEZA ? `<div class="aviso a-bad">No se pudieron borrar las fotos vencidas: ${esc(E_LIMPIEZA)}</div>` : '');
}

function pintarConfig(){
  const caja = document.getElementById('fotoConfig');
  if (!caja) return;
  if (E_CONFIG){ caja.innerHTML = `<div class="aviso a-bad">No se pudo leer la configuración: ${esc(E_CONFIG)}</div>`; return; }
  if (!CONFIG){ caja.innerHTML = '<div class="hint">Leyendo la configuración…</div>'; return; }
  const c = CONFIG;
  caja.innerHTML = `<div class="grid g2">
      <label class="fv-l">Modelo de IA que lee las fotos
        <select id="fcModelo"><option value="haiku">Haiku</option><option value="sonnet">Sonnet</option></select>
        <span class="hint">Cada foto se lee dos veces con este modelo; si las lecturas no coinciden, desempata Sonnet.</span></label>
      <div>
        <label class="fv-l" style="display:flex;gap:0.471rem;align-items:center"><input type="checkbox" id="fcGuardar" style="width:auto;display:inline"> Guardar las fotos</label>
        <label class="fv-l">Cuántos días se guardan <input type="number" id="fcDias" min="1" max="3650" step="1" inputmode="numeric" style="max-width:7.059rem"></label>
        <div id="fotoLimpieza"></div>
      </div>
      <label class="fv-l">La planilla de Google (INCOMING)
        <input type="text" id="fcPlanilla" autocomplete="off" placeholder="El identificador o el enlace entero">
        <span class="hint">Podés pegar el enlace entero de la planilla. Tiene que estar compartida, como editora, con la cuenta de servicio.</span></label>
      <div>
        <label class="fv-l">Hoja del catálogo <input type="text" id="fcHojaCat" maxlength="100" autocomplete="off"></label>
        <label class="fv-l">Hoja del Pistoleado <input type="text" id="fcHojaPis" maxlength="100" autocomplete="off"></label>
      </div>
    </div>
    <div class="fila-acc" style="justify-content:flex-start;margin-top:0.588rem">
      <button class="b b-pri" type="button" data-acc="guardar-config">Guardar la configuración</button>
      ${c.actualizado_en ? `<span class="hint">Última vez: ${esc(fecha(c.actualizado_en))}</span>` : ''}
    </div>
    <div id="fotoConfigMsg"></div>
    <h4 style="margin-top:0.824rem">Qué categorías netean juntas</h4>
    <div id="fotoGrupos"></div>`;
  $('#fcModelo').value = c.modelo_lectura;
  $('#fcGuardar').checked = c.guardar_fotos;
  $('#fcDias').value = String(c.retencion_dias);
  $('#fcPlanilla').value = c.spreadsheet_id;
  $('#fcHojaCat').value = c.hoja_catalogo;
  $('#fcHojaPis').value = c.hoja_pistoleo;
  pintarLimpieza();
  pintarGrupos();
}

async function guardarConfig(){
  if (!CONFIG) return;
  const val = (id: string) => ($(id).value ?? '').trim();
  const diasTxt = val('#fcDias');
  const dias = diasTxt === '' ? NaN : Number(diasTxt);
  if (!Number.isInteger(dias) || dias < 1){ aviso('#fotoConfigMsg', 'bad', 'Escribí cuántos días se guardan las fotos (un número entero, 1 o más).'); return; }
  const antes = CONFIG;
  const b = document.querySelector('[data-acc="guardar-config"]') as HTMLButtonElement | null;
  if (b) b.disabled = true;
  const { data, error } = await rpc('foto_guardar_config', {
    p_modelo_lectura: val('#fcModelo'), p_guardar_fotos: $('#fcGuardar').checked,
    p_retencion_dias: dias, p_spreadsheet_id: val('#fcPlanilla'), p_hoja_catalogo: val('#fcHojaCat'), p_hoja_pistoleo: val('#fcHojaPis') });
  if (error){
    if (b) b.disabled = false;
    aviso('#fotoConfigMsg', 'bad', 'No se guardó: ' + error.message);
    return;
  }
  CONFIG = data as ConfigFotos;
  pintarConfig();
  const otroCatalogo = antes.spreadsheet_id !== CONFIG.spreadsheet_id || antes.hoja_catalogo !== CONFIG.hoja_catalogo;
  aviso('#fotoConfigMsg', 'ok', ((data as any)?.mensaje ?? 'Guardada.')
    + (otroCatalogo ? ' Cambió de dónde sale el catálogo: se vuelve a leer.' : ''));
  //  El catálogo de la sesión salió de la planilla o la hoja anterior.
  if (otroCatalogo){
    CATALOGO = null;
    GUARDADO_EN.clear();
    if (REPORTE) asegurarCatalogo(true);
  }
}

// ---------------------------------------------------------------------
//  Qué categorías netean juntas (0095, D212)
// ---------------------------------------------------------------------
//  Las categorías con el mismo grupo netean entre sí; la que queda vacía,
//  sólo consigo misma (su grupo es su código: la cuenta de la 0085). Lo
//  cambia sólo el administrador global; los otros dos roles lo ven.
type CatGrupo = { id: number; codigo: string; nombre: string; grupo_neteo: string | null };
let GRUPOS: CatGrupo[] | null = null;
let E_GRUPOS = '';
let GUARDANDO_GRUPOS = false;

const normGrupo = (g: string | null | undefined) => String(g ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

async function cargarGrupos(){
  const { data, error } = await pedirTodo(() => sb.from('categorias').select('id,codigo,nombre,orden,grupo_neteo').eq('activo', true),
                                          { por: 'id', orden: ordenarPor('orden', 'id') });
  E_GRUPOS = error ? error.message : '';
  GRUPOS = error ? null : (data as CatGrupo[]);
  pintarGrupos();
}

//  Con quién netea cada una, según lo que está escrito en los casilleros
//  (sin guardar todavía): se ve el efecto antes de guardarlo.
function neteaCon(){
  const grupo = new Map<number, string>();
  document.querySelectorAll<HTMLInputElement>('#fotoGrupos [data-gcat]').forEach(i => {
    const c = GRUPOS!.find(x => x.id === Number(i.dataset.gcat));
    if (c) grupo.set(c.id, normGrupo(i.value) || c.codigo.toLowerCase());
  });
  document.querySelectorAll<HTMLElement>('#fotoGrupos [data-gcon]').forEach(td => {
    const id = Number(td.dataset.gcon), g = grupo.get(id);
    const otras = GRUPOS!.filter(c => c.id !== id && grupo.get(c.id) === g).map(c => c.nombre);
    td.innerHTML = otras.length ? esc(otras.join(', ')) : '<span class="hint">sólo consigo misma</span>';
  });
}

function pintarGrupos(){
  const caja = document.getElementById('fotoGrupos');
  if (!caja) return;
  if (E_GRUPOS){ caja.innerHTML = `<div class="aviso a-bad">No se pudieron leer las categorías: ${esc(E_GRUPOS)}</div>`; return; }
  if (!GRUPOS){ caja.innerHTML = '<div class="hint">Leyendo las categorías…</div>'; return; }
  const edita = soyAdminGlobal();
  const nombres = [...new Set(GRUPOS.map(c => normGrupo(c.grupo_neteo)).filter(Boolean))].sort();
  const dis = !edita || GUARDANDO_GRUPOS ? 'disabled' : '';
  //  Una vacía tiene por grupo su código: netea también con las que tengan
  //  ese código por grupo (iPhone vacía sigue con iPhone con SIM, «iphone»).
  caja.innerHTML = `<p class="hint">Las categorías con el mismo grupo netean entre sí en la comparativa; una vacía usa su código
      como grupo. «Netea con» muestra cómo queda. ${edita ? '' : 'Sólo el administrador global lo cambia.'}</p>
    <datalist id="dlGruposNeteo">${nombres.map(n => `<option value="${esc(n)}">`).join('')}</datalist>
    <div class="tw"><table class="t-tarjetas" id="tablaGrupos"><thead><tr><th>Categoría</th><th>Grupo</th><th>Netea con</th></tr></thead><tbody>${
      GRUPOS.map(c => `<tr><td data-label="Categoría">${esc(c.nombre)}</td>
        <td data-label="Grupo"><input type="text" data-gcat="${c.id}" list="dlGruposNeteo" maxlength="50" autocomplete="off"
             aria-label="Grupo de neteo de ${esc(c.nombre)}" value="${esc(c.grupo_neteo ?? '')}" style="max-width:10.588rem" ${dis}></td>
        <td data-label="Netea con" data-gcon="${c.id}"></td></tr>`).join('')
    }</tbody></table></div>
    ${edita ? `<div class="fila-acc" style="justify-content:flex-start;margin-top:0.471rem">
      <button class="b b-pri" type="button" data-acc="guardar-grupos" ${dis}>Guardar los grupos</button></div>` : ''}
    <div id="fotoGruposMsg"></div>`;
  caja.querySelectorAll('[data-gcat]').forEach(i => i.addEventListener('input', neteaCon));
  neteaCon();
}

async function guardarGrupos(){
  if (!GRUPOS || GUARDANDO_GRUPOS) return;
  //  Sólo las que cambiaron; la base vuelve a mirar cuáles cambian de verdad.
  const cambios = [...document.querySelectorAll<HTMLInputElement>('#fotoGrupos [data-gcat]')]
    .map(i => ({ categoria_id: Number(i.dataset.gcat), grupo: normGrupo(i.value) }))
    .filter(x => x.grupo !== normGrupo(GRUPOS!.find(c => c.id === x.categoria_id)?.grupo_neteo));
  if (!cambios.length){ aviso('#fotoGruposMsg', 'info', 'No cambiaste ningún grupo.'); return; }
  GUARDANDO_GRUPOS = true;
  pintarGruposSinPerder();
  const { data, error } = await rpc('foto_guardar_grupos_neteo', { p_grupos: cambios });
  GUARDANDO_GRUPOS = false;
  if (error){
    pintarGruposSinPerder();
    aviso('#fotoGruposMsg', 'bad', 'No se guardaron los grupos: ' + error.message);
    return;
  }
  await cargarGrupos();
  aviso('#fotoGruposMsg', 'ok', (data as any)?.mensaje ?? 'Guardados.');
  //  Las sugerencias de neteo se arman al leer la comparativa.
  if (REPORTE && COMPARA) void refrescarPestana();
}

//  Repinta (para apagar o prender el botón) sin perder lo escrito.
function pintarGruposSinPerder(){
  const escrito = new Map([...document.querySelectorAll<HTMLInputElement>('#fotoGrupos [data-gcat]')].map(i => [i.dataset.gcat!, i.value]));
  pintarGrupos();
  document.querySelectorAll<HTMLInputElement>('#fotoGrupos [data-gcat]').forEach(i => {
    if (escrito.has(i.dataset.gcat!)) i.value = escrito.get(i.dataset.gcat!)!;
  });
  if (GRUPOS) neteaCon();
}

//  Los avisos del final del reporte (plan, paso 7 del flujo).
function avisosFinales(duplicadas: number, truncadas: number, nuevos: string[]){
  const it: string[] = [];
  if (duplicadas) it.push(`${duplicadas} foto(s) duplicada(s) no suman (cada una tiene «Sumar igual»).`);
  if (truncadas) it.push(`${truncadas} línea(s) leída(s) incompletas: revisalas en la foto.`);
  if (nuevos.length) it.push(`Productos creados en este reporte (darlos de alta en el sistema actual): ${nuevos.map(esc).join(', ')}.`);
  return it.length ? `<div class="aviso a-info" style="margin-top:0.588rem">${it.map(t => '• ' + t).join('<br>')}</div>` : '';
}

// ---------------------------------------------------------------------
//  La ventana «A validar»
// ---------------------------------------------------------------------
let V: { img: Imagen; linea: Linea; elegido: Prod | null; sug: Sugerencia<Prod> | null; reemplazar: boolean; creando: boolean } | null = null;

function abrirValidar(lineaId?: number){
  let par: { img: Imagen; linea: Linea } | undefined;
  if (lineaId){
    for (const img of IMAGENES){ const l = img.lineas.find(x => x.id === lineaId); if (l){ par = { img, linea: l }; break; } }
  } else {
    par = pendientesDe()[0];
  }
  if (!par){ cerrarValidar(); aviso('#fotoColaMsg', 'ok', 'No queda ninguna línea a validar.'); return; }
  const elegido = par.linea.producto_codigo
    ? (productoDe(par.linea.producto_codigo) ?? { codigo: par.linea.producto_codigo, nombre: '', nro_modelo: null })
    : null;
  //  Sin producto todavía: se propone el más parecido del catálogo (D189).
  //  Con la planilla todavía en camino no se sugiere: sería con medio catálogo.
  const sug = elegido || CATALOGO_LEYENDO || (!CATALOGO && !NUBILON?.length) ? null : sugerir(par.linea.descripcion, par.linea.codigo_proveedor, todosLosProductos());
  V = { ...par, elegido: elegido ?? sug?.producto ?? null, sug, reemplazar: false, creando: false };
  pintarValidar();
}

function cerrarValidar(){
  V = null;
  document.getElementById('fotoValidar')?.remove();
}

function pintarValidar(){
  if (!V) return;
  let capa = document.getElementById('fotoValidar');
  if (!capa){
    capa = document.createElement('div');
    capa.id = 'fotoValidar';
    capa.className = 'foto-validar';
    capa.setAttribute('role', 'dialog');
    capa.setAttribute('aria-label', 'Validar línea');
    $('#s-fotos').appendChild(capa);
  }
  const { img, linea } = V;
  const pend = pendientesDe();
  const pos = pend.findIndex(p => p.linea.id === linea.id);
  const foto = img.miniatura
    ? `<img src="${esc(img.miniatura)}" alt="Foto ${img.orden}" data-grande="${esc(img.miniatura)}">`
    : `<div class="sin-img">${sinImagen(img)}</div>`;
  const resaltar = img.lineas.map(l => `<tr class="${l.id === linea.id ? 'esta' : ''}"><td>${l.a_mano ? '<span class="hint">(agregada a mano)</span>' : esc(l.descripcion)}</td>
      <td class="mono">${esc(l.codigo_proveedor ?? '')}</td><td class="num mono">${l.cantidad == null ? '?' : num(l.cantidad)}</td></tr>`).join('');
  const sinCatalogo = avisoSinCatalogo();
  capa.innerHTML = `<div class="fv-caja">
    <div class="fv-foto">${foto}
      <div class="tw" style="margin-top:0.471rem"><table><thead><tr><th>Leído en la foto #${img.orden}</th><th>Código</th><th class="num">Cant.</th></tr></thead>
      <tbody>${resaltar}</tbody></table></div></div>
    <div class="fv-form">
      <h3 style="margin-top:0">Foto #${img.orden} · ${pos >= 0 ? `pendiente ${pos + 1} de ${pend.length}` : esc(CHIP_LINEA[linea.estado]?.[1] ?? linea.estado)}</h3>
      <div class="fv-leido"><div class="hint">Leído</div><b>${esc(linea.descripcion)}</b>
        ${linea.codigo_proveedor ? `<div>Código: <span class="mono">${esc(linea.codigo_proveedor)}</span></div>` : '<div class="hint">Sin código</div>'}
        ${linea.truncada ? '<div class="chip c-warn">línea incompleta: mirala en la foto</div>' : ''}
        ${img.estado === 'a_validar' ? '<div class="chip c-warn">foto en duda: revisá la cantidad</div>' : ''}</div>
      ${sinCatalogo}
      <label class="fv-l">Producto del catálogo
        <input type="search" id="fvBuscar" placeholder="Buscá por código, nombre o # Modelo" autocomplete="off"></label>
      <div id="fvResultados" class="fv-res"></div>
      <div id="fvElegido">${elegidoBloque()}</div>
      <label class="fv-l">Cantidad <input type="number" id="fvCant" min="0" max="100000" step="1" inputmode="numeric" value="${linea.cantidad ?? ''}" style="max-width:7.059rem"></label>
      <div id="fvMsg"></div>
      <div id="fvReemplazar"></div>
      <div class="fila-acc" style="justify-content:flex-start">
        <button class="b b-pri" type="button" id="fvConfirmar" ${V.elegido ? '' : 'disabled'}>Confirmar</button>
        <button class="b b-sec" type="button" id="fvCrear">Crear producto</button>
        <button class="b b-sec" type="button" id="fvIgnorar">Ignorar línea</button>
      </div>
      <div id="fvCrearForm" class="${V.creando ? '' : 'oculto'}" style="margin-top:0.588rem;border-top:1px solid var(--line);padding-top:0.588rem">
        <div class="hint">Se crea en el catálogo de Nubilon y queda en la lista del reporte para darlo de alta en el sistema actual.</div>
        <label class="fv-l">Código <input type="text" id="fvNuevoCod" maxlength="40" autocomplete="off" style="max-width:11.765rem"></label>
        <label class="fv-l">Descripción <input type="text" id="fvNuevoDesc" maxlength="200" value="${esc(linea.descripcion)}"></label>
        <button class="b b-pri" type="button" id="fvCrearOk">Crear y asignar</button>
      </div>
      <div class="fila-acc" style="justify-content:space-between;margin-top:0.824rem">
        <button class="b b-sec" type="button" id="fvSaltear" ${pend.length > 1 || pos < 0 ? '' : 'disabled'}>Saltear</button>
        <button class="b b-sec" type="button" id="fvCerrar">Cerrar</button>
      </div>
    </div></div>`;

  const q = document.getElementById('fvBuscar') as HTMLInputElement;
  //  Sin sugerencia, el buscador arranca con el código leído (o la
  //  descripción): sólo filtra, no elige.
  q.value = V.elegido ? '' : (linea.codigo_proveedor || linea.descripcion || '');
  pintarResultados();
  q.addEventListener('input', pintarResultados);
  q.focus();
}

const elegidoHtml = (p: Prod, sugerido = false) => `<div class="fv-elegido">${sugerido ? '<span class="chip c-info">sugerido</span>' : '<span class="chip c-ok">elegido</span>'} <b class="mono">${esc(p.codigo)}</b> ${esc(p.nombre)}${p.nro_modelo ? ` <span class="hint">· ${esc(p.nro_modelo)}</span>` : ''}${p.nuevo ? ' <span class="chip c-info">creado desde acá</span>' : ''}</div>`;

//  Lo elegido, y si vino de la sugerencia: sus avisos y los otros parecidos
//  (un clic los elige, como un resultado del buscador).
function elegidoBloque(){
  if (!V) return '';
  const { elegido, sug } = V;
  const esSug = !!(sug && elegido && elegido.codigo === sug.producto.codigo);
  const otros = sug ? [sug.producto, ...sug.otros].filter(p => p.codigo !== elegido?.codigo) : [];
  return (elegido ? elegidoHtml(elegido, esSug) : '<div class="hint">Ningún producto elegido.</div>')
    + (esSug ? `<div class="hint" style="margin-top:0.235rem">Sugerido por el sistema según lo leído. Revisalo y tocá «Confirmar», o elegí otro.</div>` : '')
    + (esSug && sug!.avisos.length ? `<div class="aviso a-warn" style="margin-top:0.353rem">${sug!.avisos.map(a => '⚠ ' + esc(a)).join('<br>')}</div>` : '')
    + (otros.length ? `<div class="hint" style="margin-top:0.353rem">Otros parecidos:</div><div class="fv-res fv-otros">${otros.map(p =>
        `<button type="button" class="fv-item" data-cod="${esc(p.codigo)}"><b class="mono">${esc(p.codigo)}</b> ${esc(p.nombre)}</button>`).join('')}</div>` : '');
}

function pintarResultados(){
  const q = document.getElementById('fvBuscar') as HTMLInputElement | null;
  const caja = document.getElementById('fvResultados');
  if (!q || !caja) return;
  const r = buscar(q.value);
  caja.innerHTML = !q.value.trim() ? ''
    : r.length ? r.map(p => `<button type="button" class="fv-item" data-cod="${esc(p.codigo)}"><b class="mono">${esc(p.codigo)}</b> ${esc(p.nombre)}${p.nro_modelo ? ` <span class="hint">· ${esc(p.nro_modelo)}</span>` : ''}</button>`).join('')
    : '<div class="hint">Nada coincide. Probá con menos palabras, o creá el producto.</div>';
}

async function accionValidar(tipo: 'confirmar' | 'ignorar' | 'crear'){
  if (!V) return;
  const lineaId = V.linea.id;
  const cantTxt = ((document.getElementById('fvCant') as HTMLInputElement)?.value ?? '').trim();
  const cant = cantTxt === '' ? null : Number(cantTxt);
  if (tipo !== 'ignorar' && (cant === null || !Number.isInteger(cant) || cant < 0)){
    aviso('#fvMsg', 'bad', 'Escribí la cantidad (un número entero).'); return;
  }
  const botones = Array.from(document.querySelectorAll('#fotoValidar button')) as HTMLButtonElement[];
  botones.forEach(b => b.disabled = true);
  let r: any;
  if (tipo === 'confirmar'){
    if (!V.elegido){ aviso('#fvMsg', 'bad', 'Elegí el producto.'); botones.forEach(b => b.disabled = false); return; }
    r = await rpc('foto_validar_linea', { p_linea_id: lineaId, p_producto_codigo: V.elegido.codigo,
      p_cantidad: cant ?? undefined, p_reemplazar_alias: V.reemplazar });
  } else if (tipo === 'crear'){
    const cod = ((document.getElementById('fvNuevoCod') as HTMLInputElement)?.value ?? '').trim();
    const desc = ((document.getElementById('fvNuevoDesc') as HTMLInputElement)?.value ?? '').trim();
    r = await rpc('foto_crear_producto', { p_linea_id: lineaId, p_codigo: cod, p_descripcion: desc,
      p_cantidad: cant as number, p_reemplazar_alias: V.reemplazar });
  } else {
    r = await rpc('foto_ignorar_linea', { p_linea_id: lineaId });
  }
  if (r.error){
    botones.forEach(b => b.disabled = false);
    if (!V) return;
    $('#fvConfirmar').disabled = !V.elegido;
    aviso('#fvMsg', 'bad', r.error.message);
    //  La clave ya apunta a otro producto: se ofrece reemplazarla, explícito.
    if (tipo !== 'ignorar' && /Reemplazar la asociación/.test(r.error.message) && !/«# Modelo»|la usan/.test(r.error.message)){
      $('#fvReemplazar').innerHTML = `<label class="hint" style="display:block;margin:0.353rem 0">
        <input type="checkbox" id="fvReemplazarChk"> Reemplazar la asociación: esa clave pasa al producto ${tipo === 'crear' ? 'nuevo' : esc(V.elegido?.codigo ?? '')}, y las líneas que se completaron solas con la anterior también. Después volvé a tocar «${tipo === 'crear' ? 'Crear y asignar' : 'Confirmar'}».</label>`;
      $('#fvReemplazarChk').addEventListener('change', (e: Event) => { if (V) V.reemplazar = (e.target as HTMLInputElement).checked; });
    }
    return;
  }
  const msg = (r.data as any)?.mensaje ?? 'Listo.';
  const seguia = V !== null && V.linea.id === lineaId;   // ¿la ventana sigue abierta en esta línea?
  if (tipo === 'crear'){
    const cod = ((document.getElementById('fvNuevoCod') as HTMLInputElement)?.value ?? '').trim();
    NUEVOS.push({ codigo: cod, nombre: ((document.getElementById('fvNuevoDesc') as HTMLInputElement)?.value ?? '').trim(), nro_modelo: null, nuevo: true });
  }
  if (REPORTE){
    await cargarImagenes(REPORTE.id);
    await cargarReportes();
    pintarReportes();
  }
  pintarImagenes();
  aviso('#fotoColaMsg', 'ok', msg);
  //  Sigue con la próxima pendiente, si la hay (y si el usuario no cerró la
  //  ventana mientras la base contestaba).
  if (!seguia) return;
  const sig = pendientesDe()[0];
  if (sig){ abrirValidar(sig.linea.id); aviso('#fvMsg', 'ok', msg); }
  else cerrarValidar();
}

async function accionSimple(acc: string, id: number){
  let r: any;
  if (acc === 'reabrir') r = await rpc('foto_reabrir_linea', { p_linea_id: id });
  else if (acc === 'ignorar') r = await rpc('foto_ignorar_linea', { p_linea_id: id });
  else if (acc === 'confirmar-foto') r = await rpc('foto_confirmar_imagen', { p_imagen_id: id });
  else if (acc === 'sumar' || acc === 'no-sumar') r = await rpc('foto_sumar_duplicada', { p_imagen_id: id, p_sumar: acc === 'sumar' });
  else if (acc === 'quitar'){
    const img = IMAGENES.find(i => i.id === id);
    const que = img && esSinFoto(img) ? 'lo cargado sin foto' : `la foto #${img?.orden ?? ''}`;
    if (!confirm(`¿Quitar ${que} del reporte? Sus líneas dejan de contar.`)) return;
    r = await rpc('foto_quitar_imagen', { p_imagen_id: id });
  } else return;
  if (r.error){ aviso('#fotoColaMsg', 'bad', r.error.message); return; }
  //  La base dice «Foto #0 quitada»: la carga sin foto no es una foto.
  const quitadaSinFoto = acc === 'quitar' && IMAGENES.some(i => i.id === id && esSinFoto(i));
  aviso('#fotoColaMsg', 'ok', quitadaSinFoto ? 'Lo cargado sin foto se quitó del reporte.' : (r.data as any)?.mensaje ?? 'Listo.');
  if (REPORTE){
    await cargarImagenes(REPORTE.id);
    await cargarReportes();
    pintarReportes();
  }
  pintarImagenes();
}

// ---------------------------------------------------------------------
//  Agregar una línea a mano (0081, D191)
// ---------------------------------------------------------------------
//  (0098, D217) `img` nulo: la carga sin foto (puede no existir todavía; la
//  crea la base con la primera línea).
let AG: { img: Imagen | null; elegido: Prod | null; enviando: boolean } | null = null;

const sinFotoDe = () => IMAGENES.find(esSinFoto) ?? null;

function abrirAgregar(imagenId: number | null){
  const img = imagenId == null ? sinFotoDe() : IMAGENES.find(i => i.id === imagenId);
  if (imagenId != null && !img) return;
  if (!REPORTE) return;
  //  Releer las fotos con la tanda a medio leer se llevaría las tarjetas
  //  «leyendo» (y la foto recién leída no aparecería hasta el final).
  if (LEYENDO || COLA.length){ aviso('#fotoColaMsg', 'warn', 'Esperá a que terminen de leerse las fotos.'); return; }
  cerrarValidar();
  //  Un reporte recién creado todavía no guardó el catálogo de INCOMING, y
  //  la base lo pide para aceptar un código de ahí (no hace nada si ya está).
  void asegurarCatalogo();
  AG = { img: img ?? null, elegido: null, enviando: false };
  let capa = document.getElementById('fotoAgregar');
  if (!capa){
    capa = document.createElement('div');
    capa.id = 'fotoAgregar';
    capa.className = 'foto-validar';
    capa.setAttribute('role', 'dialog');
    capa.setAttribute('aria-label', 'Agregar línea');
    $('#s-fotos').appendChild(capa);
  }
  const sinFoto = imagenId == null;
  const sinCatalogo = avisoSinCatalogo();
  capa.innerHTML = `<div class="fv-caja">
    <div class="fv-foto" id="faFoto">${agregarIzquierda()}</div>
    <div class="fv-form">
      <h3 style="margin-top:0">${sinFoto ? 'Cargar productos sin foto' : `Agregar una línea a mano · foto #${img!.orden}`}</h3>
      <div class="hint">${sinFoto
        ? 'Para lo que no viene en ninguna foto. Cada línea queda validada y suma en el reporte. La ventana queda abierta para cargar el siguiente.'
        : 'Para lo que la foto no trae o no se pudo leer. Queda validada y suma en el total de esta foto.'}</div>
      <div id="faSinCat">${sinCatalogo}</div>
      <label class="fv-l">Producto del catálogo
        <input type="search" id="faBuscar" placeholder="Buscá por código, nombre o # Modelo" autocomplete="off"></label>
      <div id="faResultados" class="fv-res"></div>
      <div id="faElegido"><div class="hint">Ningún producto elegido.</div></div>
      <label class="fv-l">Cantidad <input type="number" id="faCant" min="1" max="100000" step="1" inputmode="numeric" style="max-width:7.059rem"></label>
      <div id="faMsg"></div>
      <div class="fila-acc" style="justify-content:space-between;margin-top:0.824rem">
        <button class="b b-pri" type="button" id="faAgregar" disabled>Agregar</button>
        <button class="b b-sec" type="button" id="faCerrar">${sinFoto ? 'Listo' : 'Cerrar'}</button>
      </div>
    </div></div>`;
  const q = document.getElementById('faBuscar') as HTMLInputElement;
  q.addEventListener('input', pintarResultadosAgregar);
  q.focus();
}

//  A la izquierda de la ventana: la foto y sus líneas, o lo cargado sin foto.
function agregarIzquierda(){
  if (!AG) return '';
  const img = AG.img;
  const sinFoto = !img || esSinFoto(img);
  const foto = sinFoto ? ''
    : img.miniatura
    ? `<img src="${esc(img.miniatura)}" alt="Foto ${img.orden}" data-grande="${esc(img.miniatura)}">`
    : `<div class="sin-img">${sinImagen(img)}</div>`;
  const leidas = (img?.lineas ?? []).map(l => `<tr class="${l.estado === 'ignorada' ? 'tachada' : ''}"><td>${sinFoto ? `<span class="mono">${esc(l.producto_codigo ?? '')}</span> <span class="hint">${esc(nombreDe(l.producto_codigo ?? ''))}</span>` : l.a_mano ? '<span class="hint">(agregada a mano)</span>' : esc(l.descripcion)}</td>
      ${sinFoto ? '' : `<td class="mono">${esc(l.producto_codigo ?? '')}</td>`}<td class="num mono">${l.cantidad == null ? '?' : num(l.cantidad)}</td></tr>`).join('');
  return `${foto}
      <div class="tw"${foto ? ' style="margin-top:0.471rem"' : ''}><table><thead><tr>${sinFoto ? '<th>Cargado sin foto</th>' : `<th>En la foto #${img.orden}</th><th>Producto</th>`}<th class="num">Cant.</th></tr></thead>
      <tbody>${leidas || `<tr><td colspan="${sinFoto ? 2 : 3}" class="vacio">${sinFoto ? 'Todavía nada' : 'Sin líneas'}</td></tr>`}</tbody></table></div>`;
}

function pintarResultadosAgregar(){
  const q = document.getElementById('faBuscar') as HTMLInputElement | null;
  const caja = document.getElementById('faResultados');
  if (!q || !caja) return;
  const r = buscar(q.value);
  caja.innerHTML = !q.value.trim() ? ''
    : r.length ? r.map(p => `<button type="button" class="fv-item fa-item" data-cod="${esc(p.codigo)}"><b class="mono">${esc(p.codigo)}</b> ${esc(p.nombre)}${p.nro_modelo ? ` <span class="hint">· ${esc(p.nro_modelo)}</span>` : ''}</button>`).join('')
    : '<div class="hint">Nada coincide. Probá con menos palabras.</div>';
}

function cerrarAgregar(){
  AG = null;
  document.getElementById('fotoAgregar')?.remove();
}

async function accionAgregar(){
  //  Una sola por vez: con el pedido en viaje, otro clic duplicaría la línea.
  if (!AG || AG.enviando || !REPORTE) return;
  const sinFoto = !AG.img || esSinFoto(AG.img);
  const rep = REPORTE.id;
  const cantTxt = ((document.getElementById('faCant') as HTMLInputElement)?.value ?? '').trim();
  const cant = cantTxt === '' ? null : Number(cantTxt);
  if (!AG.elegido){ aviso('#faMsg', 'bad', 'Elegí el producto.'); return; }
  if (cant === null || !Number.isInteger(cant) || cant < 1){ aviso('#faMsg', 'bad', 'Escribí la cantidad (un número entero, 1 o más).'); return; }
  const b = $('#faAgregar');
  b.disabled = true;
  AG.enviando = true;
  //  Para saber, a la vuelta, si sigue abierta la misma ventana del mismo reporte.
  const ag = AG;
  const r = sinFoto
    ? await rpc('foto_agregar_linea_sin_foto', { p_reporte_id: rep, p_producto_codigo: AG.elegido.codigo, p_cantidad: cant })
    : await rpc('foto_agregar_linea', { p_imagen_id: AG.img!.id, p_producto_codigo: AG.elegido.codigo, p_cantidad: cant });
  if (r.error){
    if (AG === ag){ AG.enviando = false; b.disabled = false; aviso('#faMsg', 'bad', r.error.message); }
    return;
  }
  const msg = (r.data as any)?.mensaje ?? 'Línea agregada.';
  if (!sinFoto) cerrarAgregar();
  if (REPORTE?.id === rep){
    await cargarImagenes(rep);
    await cargarReportes();
    pintarReportes();
  }
  pintarImagenes();
  if (!sinFoto || AG !== ag || REPORTE?.id !== rep){ aviso('#fotoColaMsg', 'ok', msg); return; }
  //  (D217) Sin foto, la ventana queda abierta para el siguiente: se vacía
  //  lo elegido y se muestra lo cargado hasta acá.
  AG = { img: sinFotoDe(), elegido: null, enviando: false };
  $('#faFoto').innerHTML = agregarIzquierda();
  $('#faElegido').innerHTML = '<div class="hint">Ningún producto elegido.</div>';
  (document.getElementById('faCant') as HTMLInputElement).value = '';
  const q = document.getElementById('faBuscar') as HTMLInputElement;
  q.value = '';
  pintarResultadosAgregar();
  $('#faAgregar').disabled = true;
  aviso('#faMsg', 'ok', msg);
  aviso('#fotoColaMsg', 'ok', msg);
  q.focus();
}

// ---------------------------------------------------------------------
//  Cableado
// ---------------------------------------------------------------------
$('#btnFotoNuevoReporte').addEventListener('click', nuevoReporte);
$('#selFotoReporte').addEventListener('change', e =>
  elegirReporte(Number($('#selFotoReporte').value) || null));
//  (D210) Renombrar y borrar el reporte; las pestañas; los plegables.
$('#btnFotoRenombrar').addEventListener('click', abrirRenombrar);
$('#btnFotoGuardarNombre').addEventListener('click', guardarNombre);
$('#btnFotoCancelarNombre').addEventListener('click', () => { cerrarEdicion(); limpiar('#fotoRepMsg'); });
$('#inpFotoNombre').addEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key === 'Enter'){ e.preventDefault(); guardarNombre(); }
  else if (e.key === 'Escape'){ e.stopPropagation(); cerrarEdicion(); }
});
$('#btnFotoBorrar').addEventListener('click', pedirBorrar);
$('#fotoBorrarConf').addEventListener('click', (e: Event) => {
  const b = (e.target as HTMLElement).closest('button');
  if (b?.id === 'btnFotoBorrarSi') borrarReporte();
  else if (b?.id === 'btnFotoBorrarNo') cerrarEdicion();
});
$('#fotoTabs').addEventListener('click', (e: Event) => {
  const b = (e.target as HTMLElement).closest('[data-tab]') as HTMLButtonElement | null;
  if (b && !b.disabled) elegirTab(b.dataset.tab as Tab);
});
$('#fotoTabs').addEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
  const tabs = (Array.from(document.querySelectorAll('#fotoTabs [data-tab]')) as HTMLButtonElement[]).filter(b => !b.disabled);
  const k = tabs.findIndex(b => b.dataset.tab === TAB);
  const sig = tabs[(k + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
  if (sig){ e.preventDefault(); elegirTab(sig.dataset.tab as Tab); sig.focus(); }
});
//  `toggle` no sube: se escucha en la captura.
$('#s-fotos').addEventListener('toggle', (e: Event) => {
  const d = e.target as HTMLDetailsElement;
  if (d.matches?.('details.pleg[data-pleg]')) PLEG[d.dataset.pleg!] = d.open;
}, true);

const zona = $('#zonaFotos');
const inp = $('#inpFotos');
zona.addEventListener('click', () => inp.click());
zona.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' '){ e.preventDefault(); inp.click(); } });
inp.addEventListener('change', () => { if (inp.files?.length) encolar(inp.files); inp.value = ''; });
['dragenter','dragover'].forEach(ev => zona.addEventListener(ev, e => { e.preventDefault(); zona.classList.add('sobre'); }));
['dragleave','drop'].forEach(ev => zona.addEventListener(ev, e => { e.preventDefault(); zona.classList.remove('sobre'); }));
//  Soltar fotos en cualquier parte de la pestaña las toma (WhatsApp Web →
//  Nubilon sin apuntar al recuadro). El recuadro NO las encola por su cuenta:
//  el evento sube hasta la pestaña y la pestaña las encola UNA vez. (Hasta el
//  17/10 las encolaban los dos y cada foto se leía dos veces: la segunda
//  quedaba «duplicada» y se pagaba doble.) Fuera de la pestaña el navegador
//  haría lo suyo (abrir la imagen), así que se frena sólo acá.
$('#s-fotos').addEventListener('dragover', e => e.preventDefault());
$('#s-fotos').addEventListener('drop', e => {
  e.preventDefault();
  if (V || AG) return;   // con una ventana abierta no se suman fotos
  const f = (e as DragEvent).dataTransfer?.files;
  if (f?.length && REPORTE) encolar(f);
});

//  Un solo escuchador para todos los botones de la pestaña (la lista se
//  repinta entera).
$('#s-fotos').addEventListener('click', e => {
  const t = e.target as HTMLElement;
  const grande = t.closest('img[data-grande]') as HTMLImageElement | null;
  if (grande){
    const capa = document.createElement('div');
    capa.className = 'foto-grande';
    capa.innerHTML = `<img src="${esc(grande.dataset.grande)}" alt="">`;
    capa.addEventListener('click', ev => { ev.stopPropagation(); capa.remove(); });
    document.body.appendChild(capa);
    return;
  }
  //  (D210) Tocar la fila de una foto (fuera de sus botones) la abre o la cierra.
  const cab = t.closest('.foto-cab') as HTMLElement | null;
  if (cab && !t.closest('button:not(.foto-abre)')){
    const clave = (cab.closest('.foto-tarj') as HTMLElement | null)?.dataset.foto;
    if (clave) abrirFoto(clave);
    return;
  }
  const itemAg = t.closest('.fa-item') as HTMLElement | null;
  if (itemAg && AG){
    const cod = itemAg.dataset.cod ?? '';
    AG.elegido = productoDe(cod);
    $('#faElegido').innerHTML = AG.elegido ? elegidoHtml(AG.elegido) : '<div class="hint">Ningún producto elegido.</div>';
    limpiar('#faMsg');
    $('#faAgregar').disabled = !AG.elegido || AG.enviando;
    return;
  }
  const item = t.closest('.fv-item') as HTMLElement | null;
  if (item && V){
    const cod = item.dataset.cod ?? '';
    V.elegido = productoDe(cod);
    V.reemplazar = false;
    $('#fvElegido').innerHTML = elegidoBloque();
    $('#fvReemplazar').innerHTML = '';
    limpiar('#fvMsg');
    $('#fvConfirmar').disabled = !V.elegido;
    return;
  }
  const b = t.closest('button') as HTMLButtonElement | null;
  if (!b) return;
  switch (b.id){
    case 'fvConfirmar': accionValidar('confirmar'); return;
    case 'fvIgnorar':   accionValidar('ignorar'); return;
    case 'fvCrearOk':   accionValidar('crear'); return;
    case 'fvCrear':     if (V){ V.creando = !V.creando; $('#fvCrearForm').classList.toggle('oculto', !V.creando); (document.getElementById('fvNuevoCod') as HTMLInputElement)?.focus(); } return;
    case 'fvCerrar':    cerrarValidar(); return;
    case 'faAgregar':   accionAgregar(); return;
    case 'faCerrar':    cerrarAgregar(); return;
    case 'fvSaltear': {
      if (!V) return;
      const pend = pendientesDe();
      const k = pend.findIndex(p => p.linea.id === V!.linea.id);
      const sig = pend[(k + 1) % pend.length];
      if (sig) abrirValidar(sig.linea.id);
      return;
    }
  }
  const acc = b.dataset.acc;
  if (!acc) return;
  if (acc === 'validar') abrirValidar();
  else if (acc === 'resolver') abrirValidar(Number(b.dataset.linea));
  else if (acc === 'releer') asegurarCatalogo(true);
  else if (acc === 'escribir') escribirEnIncoming();
  else if (acc === 'escribir-neteos') escribirEnIncoming(true);
  else if (acc === 'pistoleo') compararPistoleado();
  else if (acc === 'ordenar') ordenarPorColumna(b.dataset.tabla ?? '', b.dataset.col ?? '');
  else if (acc === 'orden-original'){ ORDEN.dif = null; pintarPestana(); }
  else if (acc === 'orden-original-bien'){ ORDEN.bien = null; pintarPestana(); }
  else if (acc === 'copiar-imagen') copiarDiferencias();
  else if (acc === 'guardar-config') guardarConfig();
  else if (acc === 'guardar-grupos') guardarGrupos();
  else if (acc === 'netear-sug' || acc === 'rechazar-sug' || acc === 'deshacer-neteo' || acc === 'netear-mano') accionNeteo(acc, b);
  else if (acc === 'agregar') abrirAgregar(Number(b.dataset.img));
  else if (acc === 'agregar-sin-foto') abrirAgregar(null);
  else if (acc === 'reabrir' || acc === 'ignorar') accionSimple(acc, Number(b.dataset.linea));
  else accionSimple(acc, Number(b.dataset.img));
});
document.addEventListener('keydown', e => {
  if (e.key !== 'Escape' || document.querySelector('.foto-grande')) return;
  if (AG) cerrarAgregar(); else if (V) cerrarValidar();
  else if (document.getElementById('btnFotoBorrarSi')) cerrarEdicion();
});
